import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import InsertChartOutlinedRoundedIcon from "@mui/icons-material/InsertChartOutlinedRounded";
import InsertChartRoundedIcon from "@mui/icons-material/InsertChartRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import ViewHeadlineRoundedIcon from "@mui/icons-material/ViewHeadlineRounded";
import { htmlPreview, sanitizeBasicHtml } from "../htmlUtils";
import { api } from "../apiClient";
import { alpha } from "@mui/material/styles";
import useAppTheme from "../hooks/useAppTheme";
import SkeletonPageHeader from "./SkeletonPageHeader";
import SkeletonFooter from "./SkeletonFooter";
import AccountDialog from "./AccountDialog";
import ChangePasswordDialog from "./ChangePasswordDialog";
import NewAdminPanelPage from "./NewAdminPanelPage";
import DocsPage from "./DocsPage";
import ChoiceButton from "./ChoiceButton";
import { sectionTerms } from "../sectionTerms";
import {
  collapse,
  dangerIconButtonSx,
  dialogSx,
  destructiveButtonSx,
  fadeUp,
  fieldSx,
  iconButtonSx,
  outlinedButtonSx,
  panelSx,
  popIn,
  primaryButtonSx,
  secondaryButtonSx,
  sectionTitleSx,
  staggerDelay,
  statusColors,
  toggleIconButtonSx,
  uiMotion,
  uiRadii,
} from "../uiStyles";

// The in-app user guide (DocsPage) is ready but hidden for now; set to true to show its header button.
const DOCS_ENABLED = false;

const emptyTask = {
  title: "",
  description: "",
  assigneeEmail: "",
  startDate: "",
  dueAt: "",
  status: "todo",
  sectionIds: [],
};

// Accent action button with an optional soft pulsing halo (create task, start work).
const glowIconButtonSx = (color, glowing = true) => ({
  ...iconButtonSx,
  borderColor: alpha(color, 0.4),
  color,
  bgcolor: alpha(color, 0.07),
  boxShadow: glowing ? `0 0 0 3px ${alpha(color, 0.07)}, 0 6px 16px ${alpha(color, 0.16)}` : "none",
  animation: glowing ? "trackerGlow 2.4s ease-in-out infinite" : "none",
  "@keyframes trackerGlow": {
    "0%, 100%": { boxShadow: `0 0 0 2px ${alpha(color, 0.08)}, 0 5px 14px ${alpha(color, 0.14)}` },
    "50%": { boxShadow: `0 0 0 6px ${alpha(color, 0.13)}, 0 7px 22px ${alpha(color, 0.24)}` },
  },
  "&:hover": {
    bgcolor: alpha(color, 0.13),
    borderColor: alpha(color, 0.6),
    transform: "translateY(-1px)",
  },
});

// Native date input with the calendar icon in front of the date; a click anywhere in the field opens the picker.
function DateField({ label, value, onChange, disabled = false, error = false, isDark = false }) {
  const inputRef = useRef(null);
  const openPicker = () => {
    if (disabled) return;
    try { inputRef.current?.showPicker?.(); } catch { /* picker needs a user gesture; typing still works */ }
  };
  return (
    <TextField
      type="date"
      required
      label={label}
      value={value}
      disabled={disabled}
      error={error}
      inputRef={inputRef}
      onClick={openPicker}
      onChange={onChange}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CalendarTodayOutlinedIcon sx={{ fontSize: 19, color: error ? "error.main" : "text.secondary" }} />
          </InputAdornment>
        ),
      }}
      sx={{
        ...fieldSx(isDark),
        "& .MuiOutlinedInput-root, & input": { cursor: disabled ? "default" : "pointer" },
        "& input::-webkit-calendar-picker-indicator": { display: "none" },
      }}
    />
  );
}

// The one green call to action on the list page: people should find "create task" at a glance.
const CREATE_GREEN = "#16a34a";
const createTaskButtonSx = (glowing = true) => ({
  ...primaryButtonSx,
  flexShrink: 0,
  height: 40,
  minWidth: 40,
  px: { xs: 0, md: 2.25 },
  bgcolor: CREATE_GREEN,
  color: "#fff",
  whiteSpace: "nowrap",
  boxShadow: `0 6px 16px ${alpha(CREATE_GREEN, 0.28)}`,
  animation: glowing ? "trackerCreateGlow 2.4s ease-in-out infinite" : "none",
  "@keyframes trackerCreateGlow": {
    "0%, 100%": { boxShadow: `0 0 0 0 ${alpha(CREATE_GREEN, 0.22)}, 0 6px 16px ${alpha(CREATE_GREEN, 0.24)}` },
    "50%": { boxShadow: `0 0 0 6px ${alpha(CREATE_GREEN, 0)}, 0 8px 22px ${alpha(CREATE_GREEN, 0.34)}` },
  },
  "& .MuiButton-startIcon": { ml: { xs: 0, md: -0.5 }, mr: { xs: 0, md: 1 } },
  "&:hover": {
    bgcolor: "#15803d",
    boxShadow: `0 8px 22px ${alpha(CREATE_GREEN, 0.36)}`,
    transform: "translateY(-1px)",
  },
});

function ConfirmationDialog({ confirmation, onClose, tx }) {
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  useEffect(() => setComment(""), [confirmation]);
  const confirm = async () => {
    if (!confirmation?.onConfirm) return;
    if (confirmation.requiresComment && !comment.trim()) return;
    setBusy(true);
    try {
      await confirmation.onConfirm(comment.trim());
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={!!confirmation}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      sx={dialogSx}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{confirmation?.title}</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary">{confirmation?.message}</Typography>
        {confirmation?.requiresComment && (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label={tx("Комментарий с решением", "Resolution comment")}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            sx={{ mt: 2 }}
            required
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={busy} sx={secondaryButtonSx}>
          {tx("Отмена", "Cancel")}
        </Button>
        <Button
          variant="contained"
          color={confirmation?.confirmColor || "primary"}
          onClick={confirm}
          disabled={busy || (confirmation?.requiresComment && !comment.trim())}
          sx={confirmation?.confirmColor === "error" ? destructiveButtonSx : primaryButtonSx}
        >
          {confirmation?.confirmLabel || tx("Подтвердить", "Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UserNameButton({ email, name, onOpen, sx = {} }) {
  if (!email) return name || "—";
  return (
    <Button
      variant="text"
      size="small"
      onClick={(event) => {
        event.stopPropagation();
        onOpen(email);
      }}
      sx={{
        minWidth: 0,
        p: 0,
        verticalAlign: "baseline",
        textTransform: "none",
        font: "inherit",
        fontWeight: 700,
        color: "text.primary",
        ...sx,
      }}
    >
      {name || email}
    </Button>
  );
}

function UserProfileDialog({ state, canEdit, onClose, onChange, onSave, tx }) {
  const profile = state?.profile;
  return (
    <Dialog
      open={!!state?.open}
      onClose={state?.saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      sx={dialogSx}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {tx("Профиль пользователя", "User profile")}
      </DialogTitle>
      <DialogContent sx={{ pt: "12px !important" }}>
        {state?.loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : profile ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={profile.avatarUrl || ""}
                sx={{ width: 64, height: 64, fontSize: 24 }}
              >
                {(profile.displayName || profile.email || "U")
                  .charAt(0)
                  .toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {profile.displayName || profile.email}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ wordBreak: "break-word" }}
                >
                  {profile.email}
                </Typography>
              </Box>
            </Stack>
            {canEdit ? (
              <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                required
                label={tx("Фамилия", "Last name")}
                value={profile.lastName || ""}
                onChange={(e) => onChange("lastName", e.target.value)}
                disabled={!canEdit}
                fullWidth
              />
              <TextField
                required
                label={tx("Имя", "First name")}
                value={profile.firstName || ""}
                onChange={(e) => onChange("firstName", e.target.value)}
                disabled={!canEdit}
                fullWidth
              />
            </Stack>
            <TextField
              label={tx("Отчество", "Middle name")}
              value={profile.middleName || ""}
              onChange={(e) => onChange("middleName", e.target.value)}
              disabled={!canEdit}
              fullWidth
            />
            <TextField
              label={tx("Должность", "Position")}
              value={profile.position || ""}
              onChange={(e) => onChange("position", e.target.value)}
              disabled={!canEdit}
              fullWidth
            />
            <TextField
              multiline
              minRows={3}
              label={tx("Описание", "About")}
              value={profile.description || ""}
              onChange={(e) => onChange("description", e.target.value)}
              disabled={!canEdit}
              fullWidth
            />
              </>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 2,
                  p: 2,
                  borderRadius: 3,
                  bgcolor: "action.hover",
                }}
              >
                {[
                  [tx("Фамилия", "Last name"), profile.lastName],
                  [tx("Имя", "First name"), profile.firstName],
                  [tx("Отчество", "Middle name"), profile.middleName],
                  [tx("Должность", "Position"), profile.position],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography sx={{ mt: 0.25 }}>{value || "—"}</Typography>
                  </Box>
                ))}
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="caption" color="text.secondary">
                    {tx("Описание", "About")}
                  </Typography>
                  <Typography sx={{ mt: 0.25, whiteSpace: "pre-wrap" }}>
                    {profile.description || "—"}
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={state?.saving}
          sx={secondaryButtonSx}
        >
          {tx("Закрыть", "Close")}
        </Button>
        {canEdit && profile ? (
          <Button
            variant="contained"
            onClick={onSave}
            disabled={
              state?.saving ||
              !profile.firstName?.trim() ||
              !profile.lastName?.trim()
            }
            sx={primaryButtonSx}
          >
            {state?.saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              tx("Сохранить", "Save")
            )}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function localInputDate(value) {
  if (!value) return "";
  const d = new Date(value);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function localInputDay(value) {
  return localInputDate(value).slice(0, 10);
}

function formatDay(value, locale) {
  if (!value) return "—";
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(locale === "ru" ? "ru-RU" : "en-US");
}

// For timestamps (due date, completion), shown in the viewer's local day; formatDay is for plain dates.
function formatMomentDay(value, locale) {
  return value ? new Date(value).toLocaleDateString(locale === "ru" ? "ru-RU" : "en-US") : "—";
}

function commentDayKey(value) {
  return new Date(value).toDateString();
}

function commentDayLabel(value, locale, tx) {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return tx("Сегодня", "Today");
  if (date.toDateString() === yesterday.toDateString()) return tx("Вчера", "Yesterday");
  return date.toLocaleDateString(locale === "ru" ? "ru-RU" : "en-US", {
    day: "numeric",
    month: "long",
    ...(date.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
  });
}

function BasicEditor({ value, onChange, mode = "light", tx }) {
  const editorRef = useRef(null);
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || ""))
      editorRef.current.innerHTML = value || "";
  }, [value]);
  const command = (name) => {
    editorRef.current?.focus();
    document.execCommand(name, false);
    onChange(editorRef.current?.innerHTML || "");
  };
  return (
    <Box
      sx={{
        ...fieldSx(mode === "dark"),
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.25,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Button
          size="small"
          onClick={() => command("bold")}
          sx={{ minWidth: 36, fontWeight: 700 }}
        >
          B
        </Button>
        <Button
          size="small"
          onClick={() => command("italic")}
          sx={{ minWidth: 36, fontStyle: "italic" }}
        >
          I
        </Button>
        <Button
          size="small"
          onClick={() => command("underline")}
          sx={{ minWidth: 36, textDecoration: "underline" }}
        >
          U
        </Button>
        <Button size="small" onClick={() => command("insertUnorderedList")}>
          {tx("Список", "List")}
        </Button>
      </Stack>
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(event) => onChange(event.currentTarget.innerHTML)}
        data-placeholder={tx("Описание задачи", "Task description")}
        sx={{
          minHeight: 150,
          p: 1.75,
          outline: "none",
          "&:empty:before": {
            content: "attr(data-placeholder)",
            color: "text.disabled",
          },
          "& p": { my: 0.5 },
        }}
      />
    </Box>
  );
}

function TaskStatusDisplay({ status, archived, overdue, completedLate = false, tx }) {
  const statuses = {
    todo: {
      label: tx("Назначена", "Assigned"),
      icon: <AssignmentRoundedIcon />,
      color: statusColors.total,
    },
    in_progress: {
      label: tx("В работе", "In progress"),
      icon: <WorkOutlineRoundedIcon />,
      color: statusColors.active,
    },
    done: {
      label: tx("Выполнена", "Completed"),
      icon: <CheckRoundedIcon />,
      color: statusColors.completed,
    },
  };
  const current = archived
    ? {
        label: tx("В архиве", "Archived"),
        icon: <ArchiveOutlinedIcon />,
        color: "warning.main",
      }
    : overdue
      ? {
          label: tx("Просрочена", "Overdue"),
          icon: <AssignmentRoundedIcon />,
          color: statusColors.overdue,
        }
      : status === "done" && completedLate
        ? {
            label: tx("Выполнена с нарушением срока", "Completed late"),
            icon: <CheckRoundedIcon />,
            color: statusColors.completedLate,
          }
        : statuses[status] || statuses.todo;

  return (
    <Stack
      component={motion.div}
      key={current.label}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 380, damping: 24 }}
      alignItems="center"
      spacing={0.75}
      sx={{ py: 1 }}
    >
      <Box
        aria-label={current.label}
        sx={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          color: current.color,
          border: "1px solid",
          borderColor: current.color,
          "& svg": { fontSize: 20 },
        }}
      >
        {current.icon}
      </Box>
      <Typography variant="body2" sx={{ color: current.color, fontWeight: 400 }}>
        {current.label}
      </Typography>
    </Stack>
  );
}

function CommentBody({ body, own, users, onOpenProfile }) {
  const parts = String(body || "").split(/(@[A-Za-zА-Яа-яЁё0-9_.-]+)/g);
  return (
    <Typography
      component="div"
      sx={{
        fontSize: "0.875rem",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        lineHeight: 1.45,
      }}
    >
      {parts.map((part, index) => {
        const mentionName = part.startsWith("@")
          ? part.slice(1).replace(/_/g, " ")
          : "";
        const mentionedUser = mentionName
          ? users.find(
              (user) =>
                String(user.displayName || "").toLocaleLowerCase() ===
                mentionName.toLocaleLowerCase(),
            )
          : null;
        return part.startsWith("@") ? (
          <Box
            component={mentionedUser ? "button" : "span"}
            key={index}
            onClick={
              mentionedUser
                ? () => onOpenProfile(mentionedUser.email)
                : undefined
            }
            sx={{
              p: 0,
              border: 0,
              bgcolor: "transparent",
              color: own ? "inherit" : "primary.main",
              font: "inherit",
              fontWeight: 600,
              cursor: mentionedUser ? "pointer" : "inherit",
              textDecoration: mentionedUser ? "underline" : "none",
              textDecorationColor: "transparent",
              textUnderlineOffset: "2px",
              "&:hover": mentionedUser
                ? { textDecorationColor: "currentColor" }
                : undefined,
            }}
          >
            {mentionedUser?.displayName || mentionName}
          </Box>
        ) : (
          part
        );
      })}
    </Typography>
  );
}

function CommentImagePreview({ taskId, item, onDownload }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let active = true;
    let objectUrl = "";
    api
      .get(`/tasks/${taskId}/attachments/${item.id}`, { responseType: "blob" })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [taskId, item.id]);
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minWidth: { xs: 190, sm: 240 },
        maxWidth: 360,
      }}
    >
      <Box
        component="img"
        src={src || undefined}
        alt={item.filename}
        sx={{
          display: "block",
          width: "100%",
          minHeight: src ? 0 : 120,
          maxHeight: 280,
          objectFit: "cover",
          borderRadius: 2,
          bgcolor: "action.hover",
          cursor: "default",
        }}
      />
      {src && (
        <Tooltip title={item.filename}>
          <IconButton
            size="small"
            onClick={() => onDownload(item)}
            sx={{
              position: "absolute",
              right: 7,
              bottom: 7,
              bgcolor: "rgba(15,23,42,.72)",
              color: "white",
              "&:hover": { bgcolor: "rgba(15,23,42,.9)" },
            }}
          >
            <DownloadRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// The statistics bucket a task falls into; the buckets don't overlap, so they add up to the total.
function taskStatusKey(task) {
  if (task.status === "done") return task.completedLate ? "completedLate" : "completed";
  if (task.overdue || (task.dueAt && new Date(task.dueAt) < new Date())) return "overdue";
  return task.status === "in_progress" ? "active" : "assigned";
}

function taskStatusColor(task, theme) {
  return task.archived ? theme.palette.warning.main : statusColors[taskStatusKey(task)];
}

// One line per task in the compact list; the chevron unfolds the task into the full card in place.
function CompactTaskRow({ task, theme, statusText, locale, tx, selectable, selected, onSelectChange, onExpand }) {
  const color = taskStatusColor(task, theme);
  const stop = (event) => event.stopPropagation();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, pl: { xs: 1.25, sm: 1.75 }, pr: { xs: 0.5, sm: 0.75 }, py: 0.5, minHeight: 46 }}>
      {selectable && (
        <Checkbox
          size="small"
          checked={selected}
          onClick={stop}
          onChange={(event) => onSelectChange(event.target.checked)}
          inputProps={{ "aria-label": tx("Выбрать задачу", "Select task") }}
          sx={{ p: 0.25 }}
        />
      )}
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          width: { xs: 10, sm: 112 },
          height: { xs: 10, sm: 24 },
          borderRadius: uiRadii.pill,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: { xs: color, sm: alpha(color, 0.14) },
          border: { xs: 0, sm: `1px solid ${alpha(color, 0.35)}` },
          color: "text.primary",
          fontSize: 12.5,
          whiteSpace: "nowrap",
        }}
      >
        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{statusText}</Box>
      </Box>
      <Typography sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap title={task.title}>
        {task.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ display: { xs: "none", md: "block" }, width: 160, flexShrink: 0 }}>
        {task.assigneeName}
      </Typography>
      <Typography
        variant="body2"
        noWrap
        sx={{ flexShrink: 0, width: 84, textAlign: "right", color: task.overdue ? "error.main" : "text.secondary" }}
      >
        {formatMomentDay(task.dueAt, locale)}
      </Typography>
      <Box
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 0.4,
          width: 44,
          flexShrink: 0,
          color: task.unreadCommentsCount > 0 ? "success.main" : "text.disabled",
          fontSize: 13,
          fontWeight: task.unreadCommentsCount > 0 ? 700 : 400,
        }}
      >
        {task.commentsCount > 0 && (
          <>
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16 }} />
            {task.commentsCount}
          </>
        )}
      </Box>
      <Tooltip title={tx("Развернуть", "Expand")} arrow>
        <IconButton
          size="small"
          aria-label={tx("Развернуть", "Expand")}
          onClick={(event) => { stop(event); onExpand(); }}
          onPointerDown={stop}
          sx={{ flexShrink: 0, color: "text.secondary" }}
        >
          <KeyboardArrowDownRoundedIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function TaskDashboard({ tasks, tx, ready, activeKey, onSelect }) {
  const total = tasks.length;
  const count = (key) => tasks.filter((task) => taskStatusKey(task) === key).length;
  const items = [
    { key: "assigned", label: tx("Назначена", "Assigned"), value: count("assigned") },
    { key: "active", label: tx("В работе", "In progress"), value: count("active") },
    { key: "completed", label: tx("Выполнено", "Completed"), value: count("completed") },
    { key: "completedLate", label: tx("Выполнено с нарушением срока", "Completed late"), value: count("completedLate") },
    { key: "overdue", label: tx("Просрочено", "Overdue"), value: count("overdue") },
  ];
  const legendItems = items;
  const lastTwo = total % 100;
  const lastOne = total % 10;
  const taskNoun =
    lastTwo >= 11 && lastTwo <= 14
      ? tx("задач", "tasks")
      : lastOne === 1
        ? tx("задача", "task")
        : lastOne >= 2 && lastOne <= 4
          ? tx("задачи", "tasks")
          : tx("задач", "tasks");
  let offset = 0;
  const slices = items.map((item) => {
    const share = total ? (item.value / total) * 100 : 0;
    const result = { ...item, share, offset };
    offset += share;
    return result;
  });

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 12 }}
      animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.32, ease: "easeOut" }}
      elevation={0}
      sx={{ ...panelSx, p: { xs: 1.5, sm: 2.25 } }}
    >
      <Box sx={{ mb: { xs: 2.25, sm: 2.75 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          {tx("Статистика задач", "Task statistics")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {tx(
            "Распределение задач по текущему состоянию. Нажмите на статус, чтобы показать его задачи",
            "Distribution of tasks by current status. Click a status to list its tasks",
          )}
        </Typography>
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2.5, md: 4 }}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <Box
            sx={{
              width: { xs: 168, sm: 184 },
              height: { xs: 168, sm: 184 },
              position: "relative",
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 200 200"
              sx={{
                width: "100%",
                height: "100%",
                transform: "rotate(-90deg)",
                overflow: "visible",
              }}
            >
              <circle
                cx="100"
                cy="100"
                r="76"
                fill="none"
                stroke="currentColor"
                strokeWidth="29"
                style={{ color: "rgba(107,114,128,0.10)" }}
              />
              {slices
                .filter((slice) => slice.value > 0)
                .map((slice, index) => (
                  <Tooltip
                    key={slice.key}
                    arrow
                    title={`${slice.label}: ${slice.value} · ${slice.share.toFixed(1)}%`}
                  >
                    <motion.circle
                      cx="100"
                      cy="100"
                      r="76"
                      pathLength="100"
                      fill="none"
                      stroke={statusColors[slice.key]}
                      strokeWidth="28"
                      strokeLinecap="round"
                      strokeDashoffset={-slice.offset - 0.35}
                      onClick={() => onSelect?.(slice.key)}
                      style={{ cursor: "pointer" }}
                      initial={{ opacity: 0, strokeDasharray: "0 100" }}
                      animate={ready
                        ? { opacity: activeKey && activeKey !== slice.key ? 0.3 : 1, strokeDasharray: `${Math.max(slice.share - 0.7, 0.15)} ${100 - Math.max(slice.share - 0.7, 0.15)}` }
                        : { opacity: 0, strokeDasharray: "0 100" }}
                      whileHover={{ strokeWidth: 32, filter: "brightness(1.08)" }}
                      transition={{ delay: 0.12 + index * 0.08, duration: 0.55, ease: "easeOut", strokeWidth: { duration: 0.18 }, filter: { duration: 0.18 } }}
                    />
                  </Tooltip>
                ))}
            </Box>
            <Box
              sx={{
                position: "absolute",
                inset: 38,
                borderRadius: "50%",
                bgcolor: "background.paper",
                display: "grid",
                placeItems: "center",
                textAlign: "center",
                p: 0.5,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {tx("Всего", "Total")}
                </Typography>
                <Typography
                  sx={{ fontWeight: 700, fontSize: { xs: 22, sm: 24 } }}
                >
                  {total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {taskNoun}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={1.15}>
            {legendItems.map((item) => {
              const share =
                item.key === "total"
                  ? total
                    ? 100
                    : 0
                  : total
                    ? (item.value / total) * 100
                    : 0;
              return (
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, x: 10 }}
                  animate={ready ? { opacity: 1, x: 0 } : { opacity: 0, x: 10 }}
                  transition={{
                    delay: 0.08 + legendItems.indexOf(item) * 0.06,
                    duration: 0.28,
                  }}
                  key={item.key}
                  role="button"
                  tabIndex={0}
                  aria-pressed={activeKey === item.key}
                  onClick={() => onSelect?.(item.key)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect?.(item.key);
                    }
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr auto",
                      sm: "minmax(150px, 1.1fr) 2fr auto",
                    },
                    gap: 1,
                    alignItems: "center",
                    py: 0.35,
                    px: 0.75,
                    mx: -0.75,
                    borderRadius: uiRadii.item,
                    cursor: "pointer",
                    bgcolor: activeKey === item.key ? "action.selected" : "transparent",
                    transition: "background-color 0.2s ease",
                    // Dim the children: the row's own opacity is driven by the entrance animation.
                    "& > *": { opacity: activeKey && activeKey !== item.key ? 0.5 : 1, transition: "opacity 0.2s ease" },
                    "&:hover": { bgcolor: activeKey === item.key ? "action.selected" : "action.hover" },
                    "&:hover > *": { opacity: 1 },
                    "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: statusColors[item.key],
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" noWrap>
                      {item.label}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: { xs: "none", sm: "block" },
                      height: 7,
                      bgcolor: "action.hover",
                      borderRadius: 99,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      component={motion.div}
                      initial={{ width: 0 }}
                      animate={{ width: ready ? `${share}%` : 0 }}
                      whileHover={{ scaleX: 1.045, filter: "brightness(1.12) saturate(1.08)" }}
                      transition={{ delay: 0.2 + legendItems.indexOf(item) * 0.06, duration: 0.55, ease: "easeOut", scaleX: { duration: 0.22 }, filter: { duration: 0.22 } }}
                      sx={{
                        minWidth: item.value ? 4 : 0,
                        height: "100%",
                        bgcolor: statusColors[item.key],
                        borderRadius: 99,
                        transformOrigin: "left center",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                  >
                    {item.value} · {share.toFixed(1)}%
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function SkeletonPage({ onSignOut, session, onSessionUpdate }) {
  const initial = session?.settings || {};
  const initialUi = initial.uiPreferences || {};
  const [settings, setSettings] = useState(initial);
  const { theme, mode, colorMode } = useAppTheme(settings.theme || "light");
  const locale = settings.locale || "ru";
  const en = locale === "en";
  const tx = (ru, eng) => (en ? eng : ru);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [taskContextMenu, setTaskContextMenu] = useState(null);
  const longPressRef = useRef({ timer: null, triggered: false });
  const [users, setUsers] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState(initialUi.selectedSectionIds || []);
  const [selectedAssigneeEmails, setSelectedAssigneeEmails] = useState(initialUi.selectedAssigneeEmails || []);
  const [statisticsVisible, setStatisticsVisible] = useState(initialUi.statisticsVisible ?? true);
  const [showArchived, setShowArchived] = useState(initialUi.showArchived ?? false);
  const [showInProgress, setShowInProgress] = useState(initialUi.showInProgress ?? true);
  const [showAssigned, setShowAssigned] = useState(initialUi.showAssigned ?? true);
  const [showCompleted, setShowCompleted] = useState(initialUi.showCompleted ?? true);
  const [sortAnchor, setSortAnchor] = useState(null);
  const [groupAnchor, setGroupAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [overdueBannerDismissed, setOverdueBannerDismissed] = useState(false);
  const scope = "all";
  const [groupBy, setGroupBy] = useState(initialUi.groupBy || "none");
  const [sortBy, setSortBy] = useState(initialUi.sortBy || "due");
  const [searchQuery, setSearchQuery] = useState(initialUi.searchQuery || "");
  const [loading, setLoading] = useState(true);
  const [dashboardAnimationReady, setDashboardAnimationReady] = useState(false);
  const [taskDialog, setTaskDialog] = useState({
    open: false,
    task: emptyTask,
  });
  const [details, setDetails] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [comment, setComment] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [commentSending, setCommentSending] = useState(false);
  const commentFileRef = useRef(null);
  const commentInputRef = useRef(null);
  const commentComposerRef = useRef(null);
  const commentsEndRef = useRef(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [accountForm, setAccountForm] = useState(initial);
  const [adminOpen, setAdminOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [adminTab, setAdminTab] = useState("users");
  const [adminUsers, setAdminUsers] = useState([]);
  const [security, setSecurity] = useState({});
  const [newUser, setNewUser] = useState({
    open: false,
    firstName: "",
    lastName: "",
    middleName: "",
    position: "",
    description: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "executor",
  });
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [deleteUserDialog, setDeleteUserDialog] = useState({
    open: false,
    email: "",
  });
  const [confirmation, setConfirmation] = useState(null);
  const [createButtonGlow, setCreateButtonGlow] = useState(true);
  const [taskFormErrors, setTaskFormErrors] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  // null until the user picks a view, so the admin's default keeps applying.
  const [listViewChoice, setListViewChoice] = useState(["compact", "expanded"].includes(initialUi.listView) ? initialUi.listView : null);
  const [expandedTaskIds, setExpandedTaskIds] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [userProfile, setUserProfile] = useState({
    open: false,
    email: "",
    profile: null,
    loading: false,
    saving: false,
    edit: false,
  });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const freshSettings = session?.settings;
    if (!freshSettings) return;
    setSettings(freshSettings);
    setAccountForm(freshSettings);
  }, [session?.settings]);

  useEffect(() => {
    setOverdueBannerDismissed(false);
  }, [notifications.length]);

  const notify = (message, severity = "success") =>
    setSnack({ open: true, message, severity });
  useEffect(() => {
    const timer = window.setTimeout(() => setCreateButtonGlow(false), 30000);
    return () => window.clearTimeout(timer);
  }, []);
  const errorText = (error) =>
    error?.response?.data?.error || error?.message || tx("Ошибка", "Error");
  const currentRole = session?.role || (session?.isAdmin ? "admin" : "executor");
  const taskStatisticsEnabled = security.taskStatisticsVisible ?? settings.taskStatisticsVisible ?? true;
  const filterAssignedEnabled = security.taskFilterAssignedVisible ?? settings.taskFilterAssignedVisible ?? true;
  const filterInProgressEnabled = security.taskFilterInProgressVisible ?? settings.taskFilterInProgressVisible ?? true;
  const filterCompletedEnabled = security.taskFilterCompletedVisible ?? settings.taskFilterCompletedVisible ?? true;
  const defaultListView = security.taskListView ?? settings.taskListView ?? "expanded";
  const listView = listViewChoice || defaultListView;
  const defaultSectionEnabled = security.defaultSectionEnabled ?? settings.defaultSectionEnabled ?? true;
  const filterAssigneeEnabled = security.taskFilterAssigneeVisible ?? settings.taskFilterAssigneeVisible ?? true;
  const terms = sectionTerms(security.sectionTerm ?? settings.sectionTerm, locale);
  const defaultSectionIds = defaultSectionEnabled
    ? sections.filter((section) => section.isDefault).map((section) => section.id)
    : [];
  const languageSwitcherEnabled = security.languageSwitcherVisible ?? settings.languageSwitcherVisible ?? true;
  const canManageDirectory = ["admin", "owner"].includes(currentRole);
  const canCreateTasks = ["admin", "owner", "manager"].includes(currentRole);
  // Only an administrator changes the initiator of an existing task; initiators are roles that can create tasks.
  const canChangeInitiator = currentRole === "admin" && !!taskDialog.task.id;
  const initiatorUsers = users.filter((user) => ["admin", "owner", "manager"].includes(user.role));
  if (canChangeInitiator && taskDialog.task.creatorEmail && !initiatorUsers.some((user) => user.email === taskDialog.task.creatorEmail))
    initiatorUsers.push({ email: taskDialog.task.creatorEmail, displayName: taskDialog.task.creatorName || taskDialog.task.creatorEmail });
  const assignableUsers = users.filter((user) => {
    if (currentRole === "admin") return true;
    if (currentRole === "owner") return ["admin", "manager", "executor"].includes(user.role);
    if (currentRole === "manager") return user.role === "executor";
    if (currentRole === "executor") return user.email === session?.email;
    return false;
  });
  const canUpdateTaskStatus = (task) =>
    ["admin", "owner"].includes(currentRole) ||
    task?.creatorEmail === session?.email ||
    task?.assigneeEmail === session?.email;
  const canEditOrDeleteTask = (task) =>
    ["admin", "owner"].includes(currentRole) ||
    (currentRole === "manager" && task?.creatorEmail === session?.email);
  const canArchiveTask = (task) =>
    ["admin", "owner"].includes(currentRole) ||
    (currentRole === "manager" && task?.creatorEmail === session?.email);

  useEffect(() => {
    if (!selectionMode) return undefined;
    const clearSelection = (event) => {
      if (event.target.closest?.('[data-task-selection-ui="true"]')) return;
      setSelectionMode(false);
      setSelectedTaskIds([]);
    };
    document.addEventListener("pointerdown", clearSelection, true);
    return () => document.removeEventListener("pointerdown", clearSelection, true);
  }, [selectionMode]);

  const openUserProfile = async (email, edit = false) => {
    if (!email) return;
    setUserProfile({
      open: true,
      email,
      profile: null,
      loading: true,
      saving: false,
      edit,
    });
    try {
      const response = await api.get(`/users/${encodeURIComponent(email)}`);
      setUserProfile({
        open: true,
        email,
        profile: response.data,
        loading: false,
        saving: false,
        edit,
      });
    } catch (error) {
      setUserProfile((current) => ({
        ...current,
        open: false,
        loading: false,
      }));
      notify(errorText(error), "error");
    }
  };

  const saveUserProfile = async () => {
    setUserProfile((current) => ({ ...current, saving: true }));
    try {
      const response = await api.put(
        `/users/${encodeURIComponent(userProfile.email)}`,
        userProfile.profile,
      );
      setUserProfile((current) => ({
        ...current,
        profile: response.data,
        saving: false,
      }));
      if (
        userProfile.email.toLowerCase() === (session?.email || "").toLowerCase()
      ) {
        setSettings((current) => ({ ...current, ...response.data }));
        setAccountForm((current) => ({ ...current, ...response.data }));
        onSessionUpdate?.({ settings: { ...settings, ...response.data } });
      }
      await refresh();
      if (canManageDirectory) setAdminUsers((await api.get("/admin/users")).data);
      notify(tx("Профиль сохранён", "Profile saved"));
    } catch (error) {
      setUserProfile((current) => ({ ...current, saving: false }));
      notify(errorText(error), "error");
    }
  };

  const refresh = useCallback(async () => {
    try {
      const [taskRes, userRes, noticeRes, sectionRes] = await Promise.all([
        api.get("/tasks", { params: { scope } }),
        api.get("/users"),
        api.get("/notifications"),
        api.get("/sections"),
      ]);
      setTasks(taskRes.data || []);
      setUsers(userRes.data || []);
      setNotifications(noticeRes.data || []);
      setSections(sectionRes.data || []);
    } catch (error) {
      notify(errorText(error), "error");
    } finally {
      setLoading(false);
    }
  }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    setTaskDialog((current) => {
      if (!current.open || current.task.id || (current.task.sectionIds || []).length || !defaultSectionIds.length) return current;
      return { ...current, task: { ...current.task, sectionIds: defaultSectionIds } };
    });
  }, [sections, defaultSectionEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) {
      setDashboardAnimationReady(false);
      return undefined;
    }
    const timer = window.setTimeout(
      () => setDashboardAnimationReady(true),
      380,
    );
    return () => window.clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      api.put("/preferences", { selectedSectionIds, selectedAssigneeEmails, statisticsVisible, showArchived, showInProgress, showAssigned, showCompleted, groupBy, sortBy, searchQuery, ...(listViewChoice ? { listView: listViewChoice } : {}) }).catch(() => {});
    }, 350);
    return () => window.clearTimeout(timer);
  }, [selectedSectionIds, selectedAssigneeEmails, statisticsVisible, showArchived, showInProgress, showAssigned, showCompleted, groupBy, sortBy, searchQuery, listViewChoice]);

  // Assignees that occur in the loaded tasks, for the assignee filter.
  const assigneeOptions = useMemo(() => {
    const byEmail = new Map();
    tasks.forEach((task) => {
      if (task.assigneeEmail && !byEmail.has(task.assigneeEmail))
        byEmail.set(task.assigneeEmail, task.assigneeName || task.assigneeEmail);
    });
    selectedAssigneeEmails.forEach((email) => {
      if (!byEmail.has(email)) byEmail.set(email, email);
    });
    return [...byEmail.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [tasks, selectedAssigneeEmails, locale]);
  const sectionScopedTasks = useMemo(
    () =>
      tasks.filter(
        (task) =>
          (!selectedSectionIds.length ||
            task.sections?.some((section) => selectedSectionIds.includes(section.id))) &&
          (!filterAssigneeEnabled ||
            !selectedAssigneeEmails.length ||
            selectedAssigneeEmails.includes(task.assigneeEmail)),
      ),
    [tasks, selectedSectionIds, selectedAssigneeEmails, filterAssigneeEnabled],
  );
  const sorted = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase(locale);
    const filtered = query
      ? sectionScopedTasks.filter((task) =>
          [
            task.title,
            task.description,
            task.assigneeName,
            task.assigneeEmail,
            task.creatorName,
            task.creatorEmail,
            task.status,
            ...(task.sections || []).map((section) => section.name),
          ]
            .filter(Boolean)
            .some((value) =>
              String(value).toLocaleLowerCase(locale).includes(query),
            ),
        )
      : sectionScopedTasks;
    const visibilityFiltered = filtered.filter((task) => {
      const overdue = task.status !== "done" && (task.overdue || (task.dueAt && new Date(task.dueAt) < new Date()));
      const assigned = task.status === "todo" && !overdue;
      const inProgress = task.status === "in_progress" && !overdue;
      const completed = task.status === "done" && !task.archived;
      // A filter whose button the admin has hidden never hides tasks.
      return (!task.archived || showArchived) &&
        (!assigned || !filterAssignedEnabled || showAssigned) &&
        (!inProgress || !filterInProgressEnabled || showInProgress) &&
        (!completed || !filterCompletedEnabled || showCompleted);
    });

    return [...visibilityFiltered].sort((a, b) => {
      if (sortBy === "created")
        return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === "assignee")
        return a.assigneeName.localeCompare(b.assigneeName, locale);
      return new Date(a.dueAt) - new Date(b.dueAt);
    });
  }, [sectionScopedTasks, searchQuery, sortBy, locale, showArchived, showInProgress, showAssigned, showCompleted, filterAssignedEnabled, filterInProgressEnabled, filterCompletedEnabled]);

  // A status picked in the statistics narrows only the list; the statistics keep showing every status.
  const listedTasks = useMemo(
    () => (statusFilter ? sorted.filter((task) => !task.archived && taskStatusKey(task) === statusFilter) : sorted),
    [sorted, statusFilter],
  );
  useEffect(() => {
    if (!taskStatisticsEnabled || !statisticsVisible) setStatusFilter(null);
  }, [taskStatisticsEnabled, statisticsVisible]);
  const groups = useMemo(() => {
    if (groupBy === "none") return [["", listedTasks]];
    if (groupBy === "status") {
      // Same buckets and names as the statistics legend; urgent groups first, archive last.
      const order = [
        ["overdue", tx("Просрочено", "Overdue")],
        ["active", tx("В работе", "In progress")],
        ["assigned", tx("Назначена", "Assigned")],
        ["completedLate", tx("Выполнено с нарушением срока", "Completed late")],
        ["completed", tx("Выполнено", "Completed")],
        ["archived", tx("В архиве", "Archived")],
      ];
      const buckets = {};
      listedTasks.forEach((task) => {
        (buckets[task.archived ? "archived" : taskStatusKey(task)] ||= []).push(task);
      });
      return order.filter(([key]) => buckets[key]).map(([key, label]) => [label, buckets[key]]);
    }
    const values = {};
    listedTasks.forEach((task) => {
      const key =
        groupBy === "assignee"
          ? task.assigneeName
          : new Date(task.dueAt).toLocaleDateString(
              locale === "ru" ? "ru-RU" : "en-US",
            );
      (values[key] ||= []).push(task);
    });
    return Object.entries(values);
  }, [listedTasks, groupBy, locale]); // eslint-disable-line react-hooks/exhaustive-deps

  const taskMissingFields = [
    !taskDialog.task.title?.trim() && tx("название", "title"),
    !taskDialog.task.assigneeEmail && tx("исполнитель", "executor"),
    !(taskDialog.task.sectionIds || []).length && terms.missing,
    !taskDialog.task.startDate && tx("дата начала", "start date"),
    !taskDialog.task.dueAt && tx("срок", "due date"),
  ].filter(Boolean);
  const setRoute = (path, replace = false) => {
    if (window.location.pathname !== path)
      window.history[replace ? "replaceState" : "pushState"]({}, "", path);
  };
  const closeTaskPage = () => {
    setTaskDialog({ open: false, task: emptyTask });
    setDetails(null);
    setPendingFiles([]);
    setAttachments([]);
    setRoute("/", true);
  };
  const openCreate = () => {
    if (!canCreateTasks) return;
    setRoute("/tasks/new");
    setPendingFiles([]);
    setTaskFormErrors(false);
    setTaskDialog({
      open: true,
      task: {
        ...emptyTask,
        // The executor is chosen explicitly every time, so tasks don't silently land on the first user in the list.
        assigneeEmail: "",
        startDate: localInputDate(new Date()).slice(0, 10),
        dueAt: localInputDay(new Date(Date.now() + 86400000)),
        sectionIds: defaultSectionIds,
      },
    });
    window.scrollTo({ top: 0, behavior: "auto" });
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "auto" }));
  };
  const openEdit = (task) => {
    setRoute(`/tasks/${task.id}/edit`);
    setTaskFormErrors(false);
    setTaskDialog({
      open: true,
      task: {
        ...task,
        sectionIds: (task.sections || []).map((section) => section.id),
        dueAt: localInputDay(task.dueAt),
      },
    });
    window.scrollTo({ top: 0, behavior: "auto" });
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "auto" }));
  };
  const saveTask = async () => {
    if (taskMissingFields.length) {
      setTaskFormErrors(true);
      notify(tx(`Заполните: ${taskMissingFields.join(", ")}`, `Fill in: ${taskMissingFields.join(", ")}`), "error");
      return;
    }
    try {
      const { priority: _legacyPriority, ...taskWithoutPriority } = taskDialog.task;
      const payload = {
        ...taskWithoutPriority,
        description: sanitizeBasicHtml(taskDialog.task.description),
        dueAt: new Date(`${taskDialog.task.dueAt}T23:59:59`).toISOString(),
      };
      let taskId = payload.id;
      const creating = !taskId;
      if (taskId) await api.put(`/tasks/${taskId}`, payload);
      else taskId = (await api.post("/tasks", payload)).data.id;
      for (const file of pendingFiles) {
        const form = new FormData();
        form.append("file", file);
        await api.post(`/tasks/${taskId}/attachments`, form);
      }
      setTaskDialog({ open: false, task: emptyTask });
      setPendingFiles([]);
      notify(tx("Задача сохранена", "Task saved"));
      if (creating) {
        setRoute("/", true);
        await refresh();
        return;
      }
      setRoute(`/tasks/${taskId}`, true);
      await refresh();
      await openDetails({ id: taskId });
    } catch (error) {
      notify(errorText(error), "error");
    }
  };
  const setStatus = (task, status) => {
    const statusNames = {
      todo: tx("Назначена", "Assigned"),
      in_progress: tx("В работе", "In progress"),
      done: tx("Выполнена", "Completed"),
    };
    setConfirmation({
      title: tx("Изменить статус задачи?", "Change task status?"),
      message: tx(
        `Статус задачи «${task.title}» будет изменён на «${statusNames[status]}»`,
        `Task “${task.title}” will be changed to “${statusNames[status]}”`,
      ),
      confirmLabel: statusNames[status],
      confirmColor: "primary",
      requiresComment: status === "done" && task.status !== "done",
      onConfirm: async (resolutionComment) => {
        try {
          await api.put(`/tasks/${task.id}`, {
            status,
            ...(resolutionComment ? { resolutionComment } : {}),
          });
          const [taskRes, commentRes] = await Promise.all([
            api.get(`/tasks/${task.id}`),
            api.get(`/tasks/${task.id}/comments`),
          ]);
          setComments(commentRes.data || []);
          await refresh();
          // Re-read the task: completion date and the "completed late" flag are set by the server.
          setDetails((current) => current?.id === task.id ? taskRes.data : current);
        } catch (e) {
          notify(errorText(e), "error");
          throw e;
        }
      },
    });
  };
  const removeTask = async (task) => {
    setConfirmation({
      title: tx("Удалить задачу?", "Delete task?"),
      message: tx(
        `Задача «${task.title}» будет удалена`,
        `Task “${task.title}” will be deleted`,
      ),
      confirmLabel: tx("Удалить", "Delete"),
      onConfirm: async () => {
        try {
          await api.delete(`/tasks/${task.id}`);
          setDetails(null);
          await refresh();
        } catch (e) {
          notify(errorText(e), "error");
          throw e;
        }
      },
    });
  };
  const removeComment = (commentItem) => {
    const taskId = details.id;
    setConfirmation({
      title: tx("Удалить комментарий?", "Delete comment?"),
      message: tx(
        "Комментарий и прикреплённые к нему файлы будут удалены без возможности восстановления",
        "The comment and its attached files will be deleted permanently",
      ),
      confirmLabel: tx("Удалить", "Delete"),
      onConfirm: async () => {
        try {
          await api.delete(`/tasks/${taskId}/comments/${commentItem.id}`);
          const [commentRes, attachmentRes] = await Promise.all([
            api.get(`/tasks/${taskId}/comments`),
            api.get(`/tasks/${taskId}/attachments`),
          ]);
          setComments(commentRes.data || []);
          setAttachments(attachmentRes.data || []);
          await refresh();
        } catch (e) {
          notify(errorText(e), "error");
          throw e;
        }
      },
    });
  };
  const toggleArchiveTask = (task) => {
    const restoring = !!task.archived;
    setConfirmation({
      title: restoring ? tx("Вернуть задачу из архива?", "Restore task from archive?") : tx("Переместить задачу в архив?", "Archive task?"),
      message: restoring
        ? tx(`Задача «${task.title}» снова появится в списке активных задач`, `Task “${task.title}” will return to the active task list`)
        : tx(`Задача «${task.title}» будет исключена из активных задач и статистики`, `Task “${task.title}” will be removed from active tasks and statistics`),
      confirmLabel: restoring ? tx("Вернуть", "Restore") : tx("В архив", "Archive"),
      confirmColor: "warning",
      onConfirm: async () => {
        try {
          await api.put(`/tasks/${task.id}`, { archived: !task.archived });
          const commentRes = await api.get(`/tasks/${task.id}/comments`);
          setComments(commentRes.data || []);
          setDetails((current) => current ? { ...current, archived: !current.archived } : current);
          await refresh();
          notify(restoring ? tx("Задача возвращена из архива", "Task restored") : tx("Задача перемещена в архив", "Task archived"));
        } catch (e) {
          notify(errorText(e), "error");
          throw e;
        }
      },
    });
  };
  const runBulkAction = (action) => {
    if (!selectedTaskIds.length) return;
    const deleting = action === "delete";
    setConfirmation({
      title: deleting ? tx("Удалить выбранные задачи?", "Delete selected tasks?") : tx("Архивировать выбранные задачи?", "Archive selected tasks?"),
      message: tx(`Выбрано задач: ${selectedTaskIds.length}`, `${selectedTaskIds.length} task(s) selected`),
      confirmLabel: deleting ? tx("Удалить", "Delete") : tx("В архив", "Archive"),
      confirmColor: deleting ? "error" : "warning",
      onConfirm: async () => {
        try {
          await api.post("/tasks/bulk", { taskIds: selectedTaskIds, action });
          setSelectedTaskIds([]);
          setSelectionMode(false);
          await refresh();
          notify(deleting ? tx("Задачи удалены", "Tasks deleted") : tx("Задачи перемещены в архив", "Tasks archived"));
        } catch (error) {
          notify(errorText(error), "error");
          throw error;
        }
      },
    });
  };
  const openTaskContextMenu = (event, task) => {
    if (!canEditOrDeleteTask(task)) return;
    event.preventDefault();
    event.stopPropagation();
    setTaskContextMenu({ task, mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
  };
  const startLongPress = (event, task) => {
    if (event.pointerType === "mouse" || !canEditOrDeleteTask(task)) return;
    longPressRef.current.triggered = false;
    longPressRef.current.timer = window.setTimeout(() => {
      longPressRef.current.triggered = true;
      setTaskContextMenu({ task, mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
    }, 550);
  };
  const cancelLongPress = () => {
    if (longPressRef.current.timer) window.clearTimeout(longPressRef.current.timer);
    longPressRef.current.timer = null;
  };
  const selectFromContextMenu = () => {
    const task = taskContextMenu?.task;
    setTaskContextMenu(null);
    if (!task) return;
    setSelectionMode(true);
    setSelectedTaskIds([task.id]);
  };
  const handleTaskClick = (task) => {
    if (longPressRef.current.triggered) {
      longPressRef.current.triggered = false;
      return;
    }
    if (selectionMode && canEditOrDeleteTask(task)) {
      setSelectedTaskIds((current) => current.includes(task.id) ? current.filter((id) => id !== task.id) : [...current, task.id]);
      return;
    }
    openDetails(task);
  };
  const openDetails = async (task) => {
    setRoute(`/tasks/${task.id}`);
    try {
      const [taskRes, commentRes, attachmentRes] = await Promise.all([
        api.get(`/tasks/${task.id}`),
        api.get(`/tasks/${task.id}/comments`),
        api.get(`/tasks/${task.id}/attachments`),
      ]);
      setDetails(taskRes.data);
      setComments(commentRes.data || []);
      setAttachments(attachmentRes.data || []);
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
        document.querySelector('[role="dialog"]')?.scrollTo?.({ top: 0 });
      });
      setTasks((current) =>
        current.map((item) =>
          item.id === task.id ? { ...item, unreadCommentsCount: 0 } : item,
        ),
      );
    } catch (e) {
      notify(errorText(e), "error");
    }
  };
  useEffect(() => {
    const applyPath = () => {
      // Emails sent before 1.0.21 link to /?task=<id>.
      const legacyTaskId = new URLSearchParams(window.location.search).get("task");
      if (/^\d+$/.test(legacyTaskId || "")) setRoute(`/tasks/${legacyTaskId}`, true);
      const match = window.location.pathname.match(
        /^\/tasks\/(\d+)(?:\/edit)?$/,
      );
      if (window.location.pathname === "/tasks/new") openCreate();
      else if (match) openDetails({ id: Number(match[1]) });
      else {
        setDetails(null);
        setTaskDialog((current) => ({ ...current, open: false }));
      }
    };
    applyPath();
    window.addEventListener("popstate", applyPath);
    return () => window.removeEventListener("popstate", applyPath);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // The role is known only after /me resolves, so a direct /tasks/new link is handled here.
    if (window.location.pathname !== "/tasks/new" || taskDialog.open || !session?.email) return;
    if (canCreateTasks) openCreate();
    else setRoute("/", true);
  }, [canCreateTasks, session?.email]); // eslint-disable-line react-hooks/exhaustive-deps
  const sendComment = async () => {
    if (commentSending || (!comment.trim() && !commentFiles.length)) return;
    setCommentSending(true);
    try {
      const r = await api.post(`/tasks/${details.id}/comments`, {
        body: comment.trim() || "__attachment__",
      });
      const commentId = r.data.createdCommentId;
      for (const file of commentFiles) {
        const form = new FormData();
        form.append("file", file);
        form.append("commentId", commentId);
        await api.post(`/tasks/${details.id}/attachments`, form);
      }
      const [commentRes, attachmentRes] = await Promise.all([
        api.get(`/tasks/${details.id}/comments`),
        api.get(`/tasks/${details.id}/attachments`),
      ]);
      setComments(commentRes.data || []);
      setAttachments(attachmentRes.data || []);
      setComment("");
      setCommentFiles([]);
      await refresh();
    } catch (e) {
      notify(errorText(e), "error");
    } finally {
      setCommentSending(false);
    }
  };
  const mentionMatch = comment.match(/(?:^|\s)@([^\s@]*)$/);
  const mentionUsers = mentionMatch
    ? users
        .filter((user) => {
          const query = mentionMatch[1].toLocaleLowerCase(locale);
          return (
            !query ||
            `${user.displayName || ""} ${user.email || ""}`
              .toLocaleLowerCase(locale)
              .includes(query)
          );
        })
        .slice(0, 6)
    : [];
  const insertMention = (user) => {
    const mention = `${String(user.displayName || user.email).trim()} `;
    setComment((current) => current.replace(/@[^\s@]*$/, mention));
    requestAnimationFrame(() => commentInputRef.current?.focus());
  };
  const revealCommentComposer = () => {
    const reveal = () =>
      commentComposerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    requestAnimationFrame(reveal);
    window.setTimeout(reveal, 320);
  };
  const downloadAttachment = async (item) => {
    try {
      const response = await api.get(
        `/tasks/${details.id}/attachments/${item.id}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      notify(errorText(e), "error");
    }
  };

  const saveAccount = async () => {
    try {
      const r = await api.post("/settings", accountForm);
      setSettings(r.data);
      setAccountForm(r.data);
      onSessionUpdate?.({ settings: r.data, avatarUrl: r.data.avatarUrl });
      setAccountOpen(false);
      notify(tx("Настройки сохранены", "Settings saved"));
    } catch (e) {
      notify(errorText(e), "error");
    }
  };
  const uploadAvatar = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () =>
      setAccountForm((v) => ({ ...v, avatarUrl: reader.result }));
    reader.readAsDataURL(file);
  };
  const toggleTheme = async () => {
    const next = mode === "dark" ? "light" : "dark";
    colorMode.setColorMode(next);
    const nextSettings = { ...settings, theme: next };
    setSettings(nextSettings);
    setAccountForm(nextSettings);
    try {
      const r = await api.post("/settings", nextSettings);
      onSessionUpdate?.({ settings: r.data });
    } catch {}
  };
  const changeLocale = async (next) => {
    const s = { ...settings, locale: next };
    setSettings(s);
    setAccountForm(s);
    try {
      const r = await api.post("/settings", s);
      onSessionUpdate?.({ settings: r.data });
    } catch {}
  };

  const openAdmin = async (tab) => {
    setDocsOpen(false);
    setAdminOpen(true);
    const initialTab = tab || (currentRole === "admin" ? "general" : "sections");
    setAdminTab(initialTab);
    try {
      const u = await api.get("/admin/users");
      setAdminUsers(u.data);
      if (currentRole === "admin") {
        const s = await api.get("/admin/security");
        setSecurity(s.data);
      }
    } catch (e) {
      notify(errorText(e), "error");
    }
  };
  const createUser = async () => {
    try {
      await api.post("/admin/users", newUser);
      setNewUser({
        open: false,
        firstName: "",
        lastName: "",
        middleName: "",
        position: "",
        description: "",
        email: "",
        password: "",
        confirmPassword: "",
        role: "executor",
      });
      setAdminUsers((await api.get("/admin/users")).data);
      notify(tx("Пользователь создан", "User created"));
    } catch (e) {
      notify(errorText(e), "error");
    }
  };
  const changeRole = async (email, role) => {
    try {
      await api.put(`/admin/users/${encodeURIComponent(email)}/role`, { role });
      setAdminUsers((await api.get("/admin/users")).data);
    } catch (e) {
      notify(errorText(e), "error");
    }
  };
  const deactivate = async (email) => {
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`);
      setAdminUsers((await api.get("/admin/users")).data);
      notify(tx("Пользователь удалён", "User deleted"));
      return true;
    } catch (e) {
      notify(errorText(e), "error");
      return false;
    }
  };
  const saveSecurity = async (changes = {}) => {
    try {
      const next = { ...security, ...changes };
      const saved = (await api.put("/admin/security", next)).data;
      setSecurity(saved);
      setSettings((current) => ({
        ...current,
        taskStatisticsVisible: saved.taskStatisticsVisible,
        taskFilterAssignedVisible: saved.taskFilterAssignedVisible,
        taskFilterInProgressVisible: saved.taskFilterInProgressVisible,
        taskFilterCompletedVisible: saved.taskFilterCompletedVisible,
        taskListView: saved.taskListView,
        sectionTerm: saved.sectionTerm,
        defaultSectionEnabled: saved.defaultSectionEnabled,
        taskFilterAssigneeVisible: saved.taskFilterAssigneeVisible,
        languageSwitcherVisible: saved.languageSwitcherVisible,
      }));
      notify(tx("Настройки сохранены", "Settings saved"));
    } catch (e) {
      notify(errorText(e), "error");
    }
  };
  const checkMail = async () => {
    try {
      await api.post("/admin/email/check");
      notify(tx("Подключение к SMTP успешно", "SMTP connection succeeded"));
    } catch (e) {
      notify(errorText(e), "error");
    }
  };
  const testMail = async (email) => {
    try {
      await api.post("/admin/email/test", { email: email || session?.email });
      notify(tx("Тестовое письмо отправлено", "Test email sent"));
    } catch (e) {
      notify(errorText(e), "error");
    }
  };
  const changeAdminPassword = async () => {
    try {
      await api.put(
        `/admin/users/${encodeURIComponent(passwordDialog.email)}/password`,
        { password: passwordDialog.password },
      );
      setPasswordDialog({
        open: false,
        email: "",
        password: "",
        confirmPassword: "",
      });
      notify(tx("Пароль изменён", "Password changed"));
    } catch (e) {
      notify(errorText(e), "error");
    }
  };

  const listVisible = !(adminOpen || docsOpen || details || taskDialog.open);
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const statusLabel = (status) =>
    ({
      todo: tx("Назначена", "Assigned"),
      in_progress: tx("В работе", "In progress"),
      done: tx("Выполнена", "Completed"),
    })[status];
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SkeletonPageHeader
          onToggleTheme={toggleTheme}
          onOpenAccount={() => {
            setAccountForm(settings);
            setAccountOpen(true);
          }}
          onOpenAdminPanel={openAdmin}
          onOpenDocs={DOCS_ENABLED ? () => { setAdminOpen(false); setDocsOpen(true); window.scrollTo({ top: 0 }); } : undefined}
          onSignOut={onSignOut}
          onLocaleChange={changeLocale}
          currentLocale={locale}
          currentTheme={mode}
          canManage={canManageDirectory}
          showLanguageSwitcher={languageSwitcherEnabled}
          managementLabel={tx("Панель управления", "Management")}
          accountName={(settings.displayName ||
            session?.email ||
            "U")[0].toUpperCase()}
          accountAvatarUrl={settings.avatarUrl}
        />
        {docsOpen && (
          <DocsPage role={currentRole} onBack={() => setDocsOpen(false)} />
        )}
        {adminOpen && (
          <NewAdminPanelPage
            activeSection={adminTab}
            currentLocale={locale}
            currentTheme={mode}
            currentUserEmail={session?.email || ""}
            currentRole={currentRole}
            securitySettings={security}
            onSectionChange={setAdminTab}
            onBack={() => setAdminOpen(false)}
            users={adminUsers}
            loading={loading}
            createForm={newUser}
            passwordDialog={passwordDialog}
            deleteUserDialog={deleteUserDialog}
            allowRememberMe={security.allowRememberMe}
            sessionDurationMinutes={security.sessionDurationMinutes}
            defaultTheme={security.defaultTheme}
            defaultLocale={security.defaultLocale}
            onSecuritySettingsChange={(key, value) =>
              setSecurity((current) => ({ ...current, [key]: value }))
            }
            onSecuritySettingsSave={saveSecurity}
            onCheckEmailSettings={checkMail}
            onSendTestEmail={testMail}
            onOpenNewUser={() =>
              setNewUser((current) => ({ ...current, open: true }))
            }
            onCloseNewUser={() =>
              setNewUser((current) => ({ ...current, open: false }))
            }
            onCreateUserFieldChange={(key, value) =>
              setNewUser((current) => ({ ...current, [key]: value }))
            }
            onCreateUser={createUser}
            onRoleChange={changeRole}
            onOpenProfile={(email) => openUserProfile(email, true)}
            onOpenPasswordDialog={(email) =>
              setPasswordDialog({
                open: true,
                email,
                password: "",
                confirmPassword: "",
              })
            }
            onClosePasswordDialog={() =>
              setPasswordDialog({
                open: false,
                email: "",
                password: "",
                confirmPassword: "",
              })
            }
            onPasswordFieldChange={(email, password) =>
              setPasswordDialog((current) => ({ ...current, email, password }))
            }
            onPasswordConfirmChange={(email, confirmPassword) =>
              setPasswordDialog((current) => ({
                ...current,
                email,
                confirmPassword,
              }))
            }
            onPasswordSave={changeAdminPassword}
            onOpenDeleteDialog={(email) =>
              setDeleteUserDialog({ open: true, email })
            }
            onCloseDeleteDialog={() =>
              setDeleteUserDialog({ open: false, email: "" })
            }
            onConfirmDeleteUser={async () => {
              const deleted = await deactivate(deleteUserDialog.email);
              if (deleted) {
                setDeleteUserDialog({ open: false, email: "" });
              }
            }}
            sections={sections}
            sectionTerms={terms}
            defaultSectionEnabled={defaultSectionEnabled}
            onSectionsChanged={refresh}
            onNotify={notify}
          />
        )}
        <Box
          component={motion.main}
          initial={{ opacity: 0, y: 8 }}
          animate={{
            opacity: loading || !listVisible ? 0 : 1,
            y: loading || !listVisible ? 8 : 0,
          }}
          transition={{ duration: uiMotion.base, ease: uiMotion.ease }}
          sx={{
            display: listVisible ? "block" : "none",
            pointerEvents: loading ? "none" : "auto",
            width: "100%",
            maxWidth: 1180,
            mx: "auto",
            px: { xs: 1.5, sm: 2, md: 3 },
            pb: 3,
            mt: 2,
            flex: 1,
          }}
        >
          <AnimatePresence initial={false}>
            {notifications.length > 0 && !overdueBannerDismissed && (
              <motion.div key="overdue-banner" {...collapse}>
                <Alert
                  severity="error"
                  icon={<NotificationsNoneRoundedIcon />}
                  onClose={() => setOverdueBannerDismissed(true)}
                  sx={{ mb: 2 }}
                >
                  {tx(
                    `Просроченных задач: ${notifications.length}`,
                    `${notifications.length} overdue task(s)`,
                  )}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>
          <Stack spacing={2}>
            <Paper
              component={motion.div}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: uiMotion.base, ease: uiMotion.ease }}
              elevation={0}
              sx={{
                ...panelSx,
                py: 1.15,
                px: { xs: 1.15, sm: 1.35 },
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.25}
                  sx={{ minWidth: 0 }}
                >
                  <Box sx={{ width: { xs: "100%", sm: 320 }, minWidth: 0 }}>
                    <ChoiceButton
                      multiple
                      label={terms.plural}
                      value={selectedSectionIds}
                      onChange={setSelectedSectionIds}
                      placeholder={terms.all}
                      resetLabel={tx("Сбросить", "Reset")}
                      options={sections.map((section) => ({
                        value: section.id,
                        label: section.name,
                        hint: defaultSectionEnabled && section.isDefault ? tx("по умолчанию", "default") : "",
                      }))}
                    />
                  </Box>
                  {filterAssigneeEnabled && (assigneeOptions.length > 1 || selectedAssigneeEmails.length > 0) && (
                    <Box sx={{ width: { xs: "100%", sm: 320 }, minWidth: 0 }}>
                      <ChoiceButton
                        multiple
                        label={tx("Исполнители", "Executors")}
                        value={selectedAssigneeEmails}
                        onChange={setSelectedAssigneeEmails}
                        placeholder={tx("Все исполнители", "All executors")}
                        resetLabel={tx("Сбросить", "Reset")}
                        options={assigneeOptions}
                      />
                    </Box>
                  )}
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                {filterAssignedEnabled && (
                  <Tooltip title={showAssigned ? tx("Скрыть назначенные задачи", "Hide assigned tasks") : tx("Показать назначенные задачи", "Show assigned tasks")}>
                    <IconButton onClick={() => setShowAssigned((value) => !value)} sx={toggleIconButtonSx(showAssigned)}>
                      <AssignmentRoundedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {filterInProgressEnabled && (
                  <Tooltip title={showInProgress ? tx("Скрыть задачи в работе", "Hide in-progress tasks") : tx("Показать задачи в работе", "Show in-progress tasks")}>
                    <IconButton onClick={() => setShowInProgress((value) => !value)} sx={toggleIconButtonSx(showInProgress)}>
                      <WorkOutlineRoundedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {filterCompletedEnabled && (
                  <Tooltip title={showCompleted ? tx("Скрыть выполненные задачи", "Hide completed tasks") : tx("Показать выполненные задачи", "Show completed tasks")}>
                    <IconButton onClick={() => setShowCompleted((value) => !value)} sx={toggleIconButtonSx(showCompleted)}>
                      <CheckRoundedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={showArchived ? tx("Скрыть архивные задачи", "Hide archived tasks") : tx("Показать архивные задачи", "Show archived tasks")}>
                  <IconButton onClick={() => setShowArchived((value) => !value)} sx={toggleIconButtonSx(showArchived, "warning.main")}>
                    {showArchived ? <Inventory2OutlinedIcon /> : <ArchiveOutlinedIcon />}
                  </IconButton>
                </Tooltip>
                {taskStatisticsEnabled && <Tooltip
                  title={
                    statisticsVisible
                      ? tx("Скрыть статистику", "Hide statistics")
                      : tx("Показать статистику", "Show statistics")
                  }
                >
                  <IconButton
                    onClick={() => setStatisticsVisible((value) => !value)}
                    sx={toggleIconButtonSx(statisticsVisible, "text.primary")}
                  >
                    {statisticsVisible ? <InsertChartRoundedIcon /> : <InsertChartOutlinedRoundedIcon />}
                  </IconButton>
                </Tooltip>}
                </Stack>
              </Stack>
            </Paper>
            <AnimatePresence initial={false}>
              {taskStatisticsEnabled && statisticsVisible && (
                  <TaskDashboard
                    tasks={sorted.filter((task) => !task.archived)}
                    tx={tx}
                    ready={dashboardAnimationReady}
                    activeKey={statusFilter}
                    onSelect={(key) => setStatusFilter((current) => (current === key ? null : key))}
                  />
              )}
            </AnimatePresence>
            <Paper
              component={motion.div}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.32, ease: "easeOut" }}
              elevation={0}
              sx={{ ...panelSx, width: "100%" }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, alignSelf: { xs: "flex-start", sm: "center" } }}>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    {tx("Задачи", "Tasks")}
                  </Typography>
                  <AnimatePresence initial={false}>
                    {statusFilter && (
                      <Box component={motion.div} key="status-filter" {...popIn} sx={{ display: "inline-flex" }}>
                        <Chip
                          size="small"
                          label={{
                            assigned: tx("Назначена", "Assigned"),
                            active: tx("В работе", "In progress"),
                            completed: tx("Выполнено", "Completed"),
                            completedLate: tx("Выполнено с нарушением срока", "Completed late"),
                            overdue: tx("Просрочено", "Overdue"),
                          }[statusFilter]}
                          onDelete={() => setStatusFilter(null)}
                          sx={{
                            bgcolor: alpha(statusColors[statusFilter], 0.14),
                            color: "text.primary",
                            border: "1px solid",
                            borderColor: alpha(statusColors[statusFilter], 0.4),
                            "& .MuiChip-deleteIcon": { color: statusColors[statusFilter] },
                          }}
                        />
                      </Box>
                    )}
                  </AnimatePresence>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  alignItems="center"
                  sx={{ width: { xs: "100%", sm: "auto" }, flexWrap: { xs: "wrap", sm: "nowrap" } }}
                >
                  <Tooltip title={listView === "compact" ? tx("Расширенный вид", "Expanded view") : tx("Компактный вид", "Compact view")}>
                    <IconButton
                      onClick={() => {
                        setListViewChoice(listView === "compact" ? "expanded" : "compact");
                        setExpandedTaskIds([]);
                      }}
                      sx={iconButtonSx}
                    >
                      {listView === "compact" ? <ViewAgendaOutlinedIcon /> : <ViewHeadlineRoundedIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tx("Сортировка", "Sort")}>
                    <IconButton onClick={(event) => setSortAnchor(event.currentTarget)} sx={iconButtonSx}><SortRoundedIcon /></IconButton>
                  </Tooltip>
                  <Menu anchorEl={sortAnchor} open={Boolean(sortAnchor)} onClose={() => setSortAnchor(null)}>
                    {[
                      ["due", tx("По сроку", "By due date")],
                      ["assignee", tx("По исполнителю", "By executor")],
                      ["created", tx("Сначала новые", "Newest first")],
                    ].map(([value, label]) => <MenuItem key={value} selected={sortBy === value} onClick={() => { setSortBy(value); setSortAnchor(null); }}>{label}</MenuItem>)}
                  </Menu>
                  <Tooltip title={tx("Группировка", "Group")}>
                    <IconButton onClick={(event) => setGroupAnchor(event.currentTarget)} sx={iconButtonSx}><WorkspacesOutlinedIcon /></IconButton>
                  </Tooltip>
                  <Menu anchorEl={groupAnchor} open={Boolean(groupAnchor)} onClose={() => setGroupAnchor(null)}>
                    {[
                      ["none", tx("Без группировки", "No grouping")],
                      ["status", tx("По статусу", "By status")],
                      ["due", tx("По сроку", "By due date")],
                      ["assignee", tx("По исполнителю", "By executor")],
                    ].map(([value, label]) => <MenuItem key={value} selected={groupBy === value} onClick={() => { setGroupBy(value); setGroupAnchor(null); }}>{label}</MenuItem>)}
                  </Menu>
                  <TextField
                    size="small"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={tx("Поиск задач", "Search tasks")}
                    inputProps={{ "aria-label": tx("Поиск задач", "Search tasks") }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      ...fieldSx(mode === "dark"),
                      width: { xs: "100%", sm: 270 },
                      order: { xs: -1, sm: 0 },
                      "& .MuiOutlinedInput-root": { borderRadius: 999 },
                    }}
                  />
                  {canCreateTasks && (
                    <Button
                      variant="contained"
                      onClick={openCreate}
                      aria-label={tx("Создать задачу", "Create task")}
                      startIcon={<AddRoundedIcon />}
                      sx={{ ...createTaskButtonSx(createButtonGlow), ml: { xs: "auto", sm: 0 } }}
                    >
                      <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                        {tx("Создать задачу", "Create task")}
                      </Box>
                    </Button>
                  )}
                </Stack>
              </Box>
              <AnimatePresence initial={false}>
                {selectionMode && (
                  <motion.div key="selection-bar" {...collapse}>
                    <Stack data-task-selection-ui="true" direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 2, p: 1.25, borderRadius: uiRadii.card, bgcolor: "action.hover" }}>
                      <Typography sx={{ flex: 1, fontWeight: 600 }}>{tx(`Выбрано: ${selectedTaskIds.length}`, `Selected: ${selectedTaskIds.length}`)}</Typography>
                      <Button disabled={!selectedTaskIds.length} startIcon={<ArchiveOutlinedIcon />} onClick={() => runBulkAction("archive")} sx={secondaryButtonSx}>{tx("В архив", "Archive")}</Button>
                      <Button disabled={!selectedTaskIds.length} color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => runBulkAction("delete")} sx={{ ...secondaryButtonSx, color: "error.main" }}>{tx("Удалить", "Delete")}</Button>
                      <Button onClick={() => { setSelectedTaskIds([]); setSelectionMode(false); }} sx={secondaryButtonSx}>{tx("Отменить выбор", "Clear")}</Button>
                    </Stack>
                  </motion.div>
                )}
              </AnimatePresence>
              {loading ? (
                <Box sx={{ py: 8, textAlign: "center" }}>
                  <CircularProgress />
                </Box>
              ) : listedTasks.length === 0 ? (
                <Box component={motion.div} {...fadeUp} sx={{ py: 8, textAlign: "center" }}>
                  <AssignmentTurnedInOutlinedIcon
                    sx={{ fontSize: 52, color: "text.disabled" }}
                  />
                  <Typography sx={{ mt: 1, fontWeight: 600 }}>
                    {searchQuery.trim() || statusFilter
                      ? tx("Задачи не найдены", "No tasks found")
                      : tx("Задач пока нет", "No tasks yet")}
                  </Typography>
                  {!searchQuery.trim() && !statusFilter && canCreateTasks && (
                    <Button onClick={openCreate} sx={{ ...secondaryButtonSx, mt: 1 }}>
                      {tx("Создать первую", "Create the first one")}
                    </Button>
                  )}
                </Box>
              ) : (
                groups.map(([group, items]) => (
                  <Box key={group || "all"} sx={{ mb: group ? 2.5 : 0 }}>
                    {group && (
                      <Typography sx={{ fontWeight: 700, mb: 1 }}>
                        {group}
                      </Typography>
                    )}
                    <Stack spacing={listView === "compact" ? 0.75 : 1.25}>
                      <AnimatePresence mode="popLayout">
                      {items.map((task, index) => (
                        <Card
                          component={motion.div}
                          layout
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.98 }}
                          whileHover={{ y: listView === "compact" ? -1 : -2 }}
                          transition={{ duration: uiMotion.base, ease: uiMotion.ease, delay: staggerDelay(index), layout: { duration: uiMotion.base, ease: uiMotion.ease } }}
                          key={task.id}
                          variant="outlined"
                          data-task-selection-ui="true"
                          onClick={() => handleTaskClick(task)}
                          onContextMenu={(event) => openTaskContextMenu(event, task)}
                          onPointerDown={(event) => startLongPress(event, task)}
                          onPointerUp={cancelLongPress}
                          onPointerCancel={cancelLongPress}
                          onPointerLeave={cancelLongPress}
                          sx={{
                            position: "relative",
                            cursor: "pointer",
                            borderColor: task.overdue ? "error.light" : "divider",
                            bgcolor: "background.default",
                            transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                            "&:hover": {
                              boxShadow: (t) => t.palette.ui.shadowHover,
                              borderColor: task.overdue ? "error.main" : "ui.borderStrong",
                            },
                          }}
                        >
                          {listView === "compact" && !expandedTaskIds.includes(task.id) ? (
                            <CompactTaskRow
                              task={task}
                              theme={theme}
                              statusText={task.archived ? tx("В архиве", "Archived") : task.overdue ? tx("Просрочена", "Overdue") : statusLabel(task.status)}
                              locale={locale}
                              tx={tx}
                              selectable={selectionMode && canEditOrDeleteTask(task)}
                              selected={selectedTaskIds.includes(task.id)}
                              onSelectChange={(checked) => setSelectedTaskIds((current) => checked ? [...current, task.id] : current.filter((id) => id !== task.id))}
                              onExpand={() => setExpandedTaskIds((current) => [...current, task.id])}
                            />
                          ) : (
                          <CardContent
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              // Leaves room for the collapse chevron pinned to the top-right corner.
                              ...(listView === "compact" ? { pr: { xs: 5, sm: 5.5 } } : {}),
                              "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                            }}
                          >
                            {listView === "compact" && (
                              // Same spot as the expand chevron of the compact row, so it does not jump.
                              <Tooltip title={tx("Свернуть", "Collapse")} arrow>
                                <IconButton
                                  size="small"
                                  aria-label={tx("Свернуть", "Collapse")}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setExpandedTaskIds((current) => current.filter((id) => id !== task.id));
                                  }}
                                  onPointerDown={(event) => event.stopPropagation()}
                                  sx={{ position: "absolute", top: 7, right: { xs: 4, sm: 6 }, color: "text.secondary" }}
                                >
                                  <KeyboardArrowUpRoundedIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              justifyContent="space-between"
                              spacing={1.5}
                            >
                              <Box sx={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: 1 }}>
                                {selectionMode && canEditOrDeleteTask(task) && (
                                  <Checkbox
                                    size="small"
                                    checked={selectedTaskIds.includes(task.id)}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(event) => setSelectedTaskIds((current) => event.target.checked ? [...current, task.id] : current.filter((id) => id !== task.id))}
                                    inputProps={{ "aria-label": tx("Выбрать задачу", "Select task") }}
                                    sx={{ p: 0.25, mt: -0.25 }}
                                  />
                                )}
                                <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    textDecoration: "none",
                                  }}
                                >
                                  {task.title}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  sx={{ mt: 1 }}
                                >
                                  <Tooltip
                                    arrow
                                    title={task.status === "done" && task.completedLate
                                      ? tx(`Выполнена с нарушением срока (срок ${formatMomentDay(task.dueAt, locale)})`, `Completed after the due date (${formatMomentDay(task.dueAt, locale)})`)
                                      : ""}
                                  >
                                    <Chip
                                      size="small"
                                      label={task.archived
                                        ? tx("В архиве", "Archived")
                                        : task.overdue
                                          ? tx("Просрочена", "Overdue")
                                          : task.status === "done" && task.completedAt
                                            ? `${statusLabel(task.status)} ${formatMomentDay(task.completedAt, locale)}`
                                            : statusLabel(task.status)}
                                      sx={{
                                        bgcolor: task.archived
                                          ? "warning.main"
                                          : task.overdue
                                          ? statusColors.overdue
                                          : task.status === "done"
                                            ? task.completedLate ? statusColors.completedLate : statusColors.completed
                                            : task.status === "in_progress"
                                              ? statusColors.active
                                              : statusColors.total,
                                        color: "#fff",
                                        fontWeight: 400,
                                      }}
                                    />
                                  </Tooltip>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={(task.sections || []).map((section) => section.name).join(", ") || terms.none}
                                  />
                                </Stack>
                                {task.description && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    noWrap
                                    sx={{ mt: 0.75 }}
                                  >
                                    {htmlPreview(task.description)}
                                  </Typography>
                                )}
                                {task.status === "done" && task.resolution && (
                                  <Box
                                    sx={{
                                      mt: 1,
                                      display: "inline-block",
                                      maxWidth: "100%",
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: "16px",
                                      bgcolor: alpha(task.completedLate ? statusColors.completedLate : statusColors.completed, 0.12),
                                      border: "1px solid",
                                      borderColor: alpha(task.completedLate ? statusColors.completedLate : statusColors.completed, 0.3),
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "text.primary",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      <Box component="span" sx={{ fontWeight: 600 }}>
                                        {tx("Решение", "Resolution")}:
                                      </Box>{" "}
                                      {task.resolution}
                                    </Typography>
                                  </Box>
                                )}
                                </Box>
                              </Box>
                              <Stack
                                spacing={0.7}
                                sx={{
                                  color: task.overdue
                                    ? "error.main"
                                    : "text.secondary",
                                  fontSize: 13,
                                  minWidth: { sm: 255 },
                                  "& > *": {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.7,
                                    minHeight: 22,
                                  },
                                }}
                              >
                                <span>
                                  <PersonOutlineRoundedIcon sx={{ fontSize: 17 }} />
                                  <Box component="span" sx={{ minWidth: 82 }}>
                                    {tx("Инициатор", "Initiator")}:
                                  </Box>
                                  <UserNameButton
                                    email={task.creatorEmail}
                                    name={task.creatorName}
                                    onOpen={openUserProfile}
                                  />
                                </span>
                                <span>
                                  <PersonOutlineRoundedIcon sx={{ fontSize: 17 }} />
                                  <Box component="span" sx={{ minWidth: 82 }}>
                                    {tx("Исполнитель", "Executor")}:
                                  </Box>
                                  <UserNameButton
                                    email={task.assigneeEmail}
                                    name={task.assigneeName}
                                    onOpen={openUserProfile}
                                  />
                                </span>
                                <span>
                                  <CalendarTodayOutlinedIcon sx={{ fontSize: 17 }} />
                                  <Box component="span" sx={{ minWidth: 82 }}>
                                    {tx("Назначена", "Assigned")}:
                                  </Box>
                                  {formatDay(task.startDate || task.createdAt, locale)}
                                </span>
                                <span>
                                  <CalendarTodayOutlinedIcon sx={{ fontSize: 17 }} />
                                  <Box component="span" sx={{ minWidth: 82 }}>
                                    {tx("Срок", "Due")}:
                                  </Box>
                                  {new Date(task.dueAt).toLocaleDateString(
                                    locale === "ru" ? "ru-RU" : "en-US",
                                  )}
                                </span>
                                <span>
                                  <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 17 }} />
                                  <Box component="span" sx={{ minWidth: 82 }}>
                                    {tx("Комментарии", "Comments")}:
                                  </Box>
                                  <Badge
                                    badgeContent={task.commentsCount}
                                    color={task.unreadCommentsCount > 0 ? "success" : "default"}
                                    showZero
                                    sx={{
                                      ml: 0.5,
                                      "& .MuiBadge-badge": {
                                        position: "static",
                                        transform: "none",
                                        minWidth: 22,
                                        height: 22,
                                        borderRadius: 11,
                                        bgcolor:
                                          task.unreadCommentsCount > 0
                                            ? "success.main"
                                            : "action.selected",
                                        color:
                                          task.unreadCommentsCount > 0
                                            ? "success.contrastText"
                                            : "text.secondary",
                                      },
                                    }}
                                  >
                                    <Box sx={{ width: 0 }} />
                                  </Badge>
                                </span>
                              </Stack>
                            </Stack>
                          </CardContent>
                          )}
                        </Card>
                      ))}
                      </AnimatePresence>
                    </Stack>
                  </Box>
                ))
              )}
              <Menu
                open={Boolean(taskContextMenu)}
                onClose={() => setTaskContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={taskContextMenu ? { top: taskContextMenu.mouseY, left: taskContextMenu.mouseX } : undefined}
                MenuListProps={{ "data-task-selection-ui": "true" }}
              >
                <MenuItem onClick={() => { const task = taskContextMenu?.task; setTaskContextMenu(null); if (task) toggleArchiveTask(task); }}>
                  <ArchiveOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                  {taskContextMenu?.task?.archived ? tx("Вернуть из архива", "Restore") : tx("В архив", "Archive")}
                </MenuItem>
                <MenuItem onClick={() => { const task = taskContextMenu?.task; setTaskContextMenu(null); if (task) removeTask(task); }} sx={{ color: "error.main" }}>
                  <DeleteOutlineRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                  {tx("Удалить", "Delete")}
                </MenuItem>
                <Divider />
                <MenuItem onClick={selectFromContextMenu}>
                  <CheckRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                  {tx("Выбрать", "Select")}
                </MenuItem>
              </Menu>
            </Paper>
          </Stack>
        </Box>

        {taskDialog.open && !adminOpen && !docsOpen && (
          <Box
            component={motion.main}
            {...fadeUp}
            sx={{
              width: "100%",
              maxWidth: 1180,
              mx: "auto",
              px: { xs: 1.5, sm: 2, md: 3 },
              pb: 3,
              mt: 2,
              flex: 1,
            }}
          >
            <Paper elevation={0} sx={{ ...panelSx, p: 0, overflow: "hidden" }}>
              <DialogTitle
                sx={{
                  pb: 0.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <IconButton onClick={closeTaskPage}>
                    <ArrowBackRoundedIcon />
                  </IconButton>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {taskDialog.task.id
                        ? tx("Редактировать задачу", "Edit task")
                        : tx("Новая задача", "New task")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                      {terms.createLead}
                    </Typography>
                  </Box>
                </Stack>
              </DialogTitle>
              <DialogContent sx={{ display: "grid", gap: 2, pt: 0.5, px: { xs: 1.5, sm: 3 }, overflowX: "hidden" }}>
                <Stack
                  spacing={2}
                  sx={{ pt: 2, width: "100%", maxWidth: 860, mx: "auto" }}
                >
                  {canChangeInitiator ? (
                    <Autocomplete
                      options={initiatorUsers}
                      disableClearable
                      value={initiatorUsers.find((user) => user.email === taskDialog.task.creatorEmail) || null}
                      onChange={(_, user) =>
                        user &&
                        setTaskDialog((current) => ({
                          ...current,
                          task: { ...current.task, creatorEmail: user.email, creatorName: user.displayName || user.email },
                        }))
                      }
                      getOptionLabel={(user) => user.displayName || user.email}
                      isOptionEqualToValue={(option, selected) => option.email === selected.email}
                      renderOption={(props, user) => (
                        <Box component="li" {...props} key={user.email}>
                          <Typography sx={{ fontWeight: 600 }}>{user.displayName || user.email}</Typography>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label={tx("Инициатор", "Initiator")} sx={fieldSx(mode === "dark")} />
                      )}
                    />
                  ) : (
                    <Box sx={{ px: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        {tx("Инициатор", "Initiator")}
                      </Typography>
                      <Typography sx={{ mt: 0.25 }}>
                        {taskDialog.task.creatorName || settings.displayName || session?.email}
                      </Typography>
                    </Box>
                  )}
                  <TextField
                    autoFocus
                    required
                    error={taskFormErrors && !taskDialog.task.title?.trim()}
                    sx={fieldSx(mode === "dark")}
                    label={tx("Название", "Title")}
                    value={taskDialog.task.title}
                    onChange={(e) =>
                      setTaskDialog((v) => ({
                        ...v,
                        task: { ...v.task, title: e.target.value },
                      }))
                    }
                  />
                  <BasicEditor
                    value={taskDialog.task.description}
                    mode={mode}
                    tx={tx}
                    onChange={(value) =>
                      setTaskDialog((v) => ({
                        ...v,
                        task: { ...v.task, description: value },
                      }))
                    }
                  />
                  <Autocomplete
                    options={assignableUsers}
                    value={
                      assignableUsers.find(
                        (user) => user.email === taskDialog.task.assigneeEmail,
                      ) || null
                    }
                    onChange={(_, user) =>
                      setTaskDialog((current) => ({
                        ...current,
                        task: {
                          ...current.task,
                          assigneeEmail: user?.email || "",
                        },
                      }))
                    }
                    getOptionLabel={(user) => user.displayName || user.email}
                    isOptionEqualToValue={(option, selected) =>
                      option.email === selected.email
                    }
                    filterOptions={(options, state) => {
                      const query = state.inputValue
                        .trim()
                        .toLocaleLowerCase(locale);
                      if (!query) return options;
                      return options.filter((user) =>
                        `${user.displayName || ""} ${user.email || ""}`
                          .toLocaleLowerCase(locale)
                          .includes(query),
                      );
                    }}
                    noOptionsText={tx(
                      "Исполнитель не найден",
                      "No executor found",
                    )}
                    renderOption={(props, user) => (
                      <Box component="li" {...props} key={user.email}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            {user.displayName || user.email}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        error={taskFormErrors && !taskDialog.task.assigneeEmail}
                        label={tx("Исполнитель", "Executor")}
                        placeholder={tx(
                          "Выберите исполнителя",
                          "Choose an executor",
                        )}
                        sx={fieldSx(mode === "dark")}
                      />
                    )}
                  />
                  <ChoiceButton
                    multiple
                    error={taskFormErrors && !(taskDialog.task.sectionIds || []).length}
                    required
                    label={terms.plural}
                    value={taskDialog.task.sectionIds || []}
                    onChange={(value) =>
                      setTaskDialog((v) => ({
                        ...v,
                        task: { ...v.task, sectionIds: value },
                      }))
                    }
                    options={sections.map((section) => ({
                      value: section.id,
                      label: section.name,
                      hint: defaultSectionEnabled && section.isDefault ? tx("по умолчанию", "default") : "",
                    }))}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ "& > *": { flex: 1, minWidth: 0 } }}>
                    <DateField
                      isDark={mode === "dark"}
                      label={tx("Дата начала", "Start date")}
                      value={taskDialog.task.startDate || ""}
                      disabled={!!taskDialog.task.id}
                      error={taskFormErrors && !taskDialog.task.startDate}
                      onChange={(e) =>
                        setTaskDialog((v) => ({
                          ...v,
                          task: { ...v.task, startDate: e.target.value },
                        }))
                      }
                    />
                    <DateField
                      isDark={mode === "dark"}
                      label={tx("Срок", "Due date")}
                      value={taskDialog.task.dueAt || ""}
                      error={taskFormErrors && !taskDialog.task.dueAt}
                      onChange={(e) =>
                        setTaskDialog((v) => ({
                          ...v,
                          task: { ...v.task, dueAt: e.target.value },
                        }))
                      }
                    />
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ "& > *": { flex: 1, minWidth: 0 } }}>
                    {taskDialog.task.id && (
                      <ChoiceButton
                        label={tx("Статус", "Status")}
                        disabled={!!taskDialog.task.archived}
                        value={taskDialog.task.status}
                        onChange={(value) =>
                          setTaskDialog((v) => ({
                            ...v,
                            task: { ...v.task, status: value },
                          }))
                        }
                        options={[
                          { value: "todo", label: statusLabel("todo") },
                          {
                            value: "in_progress",
                            label: statusLabel("in_progress"),
                          },
                          { value: "done", label: statusLabel("done") },
                        ]}
                      />
                    )}
                  </Stack>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<AttachFileRoundedIcon />}
                    sx={outlinedButtonSx}
                  >
                    {tx("Прикрепить файлы", "Attach files")}
                    <input
                      hidden
                      multiple
                      type="file"
                      onChange={(event) =>
                        setPendingFiles(Array.from(event.target.files || []))
                      }
                    />
                  </Button>
                  {pendingFiles.length > 0 && (
                    <Stack spacing={0.75}>
                      {pendingFiles.map((file) => (
                        <Typography
                          key={`${file.name}-${file.size}`}
                          variant="body2"
                          color="text.secondary"
                        >
                          {file.name} · {(file.size / 1024).toFixed(1)} KB
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions sx={{ px: { xs: 1.5, sm: 3 }, pb: 2.5, pt: 1, flexWrap: "wrap" }}>
                <Button onClick={closeTaskPage} sx={secondaryButtonSx}>
                  {tx("Отмена", "Cancel")}
                </Button>
                <Tooltip
                  arrow
                  describeChild
                  title={taskMissingFields.length ? tx(`Заполните: ${taskMissingFields.join(", ")}`, `Fill in: ${taskMissingFields.join(", ")}`) : ""}
                >
                  <Button
                    variant="contained"
                    onClick={saveTask}
                    sx={primaryButtonSx}
                  >
                    {tx("Сохранить", "Save")}
                  </Button>
                </Tooltip>
              </DialogActions>
            </Paper>
          </Box>
        )}

        {details && !adminOpen && !docsOpen && (
          <Box component={motion.main} key={`task-${details.id}`} {...fadeUp} sx={{ width: "100%", flex: 1 }}>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 1180,
                  mx: "auto",
                  px: { xs: 1.5, sm: 2, md: 3 },
                  pb: 3,
                  mt: 2,
                  flex: 1,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    ...panelSx,
                    p: 0,
                    overflow: "hidden",
                  }}
                >
                  <DialogTitle
                    sx={{
                      px: { xs: 1.5, sm: 3 },
                      py: 2.25,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "stretch", sm: "start" }}
                      spacing={1.5}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <IconButton onClick={closeTaskPage}>
                          <ArrowBackRoundedIcon />
                        </IconButton>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {details.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {tx("Инициатор", "Initiator")}:{" "}
                            <UserNameButton
                              email={details.creatorEmail}
                              name={details.creatorName}
                              onOpen={openUserProfile}
                            />
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: "flex-end", sm: "auto" } }}>
                        {!details.archived &&
                          (details.status === "todo"
                            ? details.assigneeEmail === session?.email
                            : canUpdateTaskStatus(details)) && (
                          <Tooltip
                            title={
                              details.status === "todo"
                                ? tx("Взять в работу", "Start work")
                                : details.status === "in_progress"
                                  ? tx("Выполнено", "Complete")
                                  : tx("Вернуть в работу", "Return to work")
                            }
                            arrow
                          >
                            <IconButton
                              onClick={() =>
                                setStatus(
                                  details,
                                  details.status === "todo"
                                    ? "in_progress"
                                    : details.status === "in_progress"
                                      ? "done"
                                      : "in_progress",
                                )
                              }
                              sx={details.status === "todo" ? glowIconButtonSx(theme.palette.success.main) : { ...iconButtonSx, color: "text.secondary" }}
                            >
                              {details.status === "todo" ? (
                                <WorkOutlineRoundedIcon />
                              ) : details.status === "in_progress" ? (
                                <CheckRoundedIcon />
                              ) : (
                                <ReplayRoundedIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {canArchiveTask(details) && (
                          <Tooltip title={details.archived ? tx("Вернуть из архива", "Restore from archive") : tx("В архив", "Archive")} arrow>
                            <IconButton onClick={() => toggleArchiveTask(details)} sx={{ ...iconButtonSx, color: details.archived ? "warning.main" : "text.secondary" }}>
                              <ArchiveOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canEditOrDeleteTask(details) && (
                          <>
                            <Tooltip title={tx("Редактировать", "Edit")} arrow>
                              <IconButton
                                onClick={() => openEdit(details)}
                                sx={iconButtonSx}
                              >
                                <EditOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={tx("Удалить", "Delete")} arrow>
                              <IconButton
                                color="error"
                                onClick={() => removeTask(details)}
                                sx={dangerIconButtonSx}
                              >
                                <DeleteOutlineRoundedIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </DialogTitle>
                  <DialogContent
                    sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2.5 } }}
                  >
                    <Stack
                      component={motion.div}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: uiMotion.base, ease: uiMotion.ease, delay: 0.05 }}
                      spacing={2.25}
                      sx={{ width: "100%", maxWidth: 980, mx: "auto", py: 2, "& .task-status": { order: 1 }, "& .task-description": { order: 2 }, "& .task-attachments": { order: 3 }, "& .task-comments": { order: 4 } }}
                    >
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                        <Chip
                          variant="outlined"
                          label={`${terms.singular}: ${(details.sections || []).map((section) => section.name).join(", ") || terms.none}`}
                        />
                        <Chip
                          variant="outlined"
                          label={
                            <span>
                              {tx("Исполнитель", "Executor")}:{" "}
                              <UserNameButton
                                email={details.assigneeEmail}
                                name={details.assigneeName}
                                onOpen={openUserProfile}
                              />
                            </span>
                          }
                        />
                        <Chip
                          variant="outlined"
                          label={`${tx("Назначена", "Assigned")}: ${formatDay(details.startDate || details.createdAt, locale)}`}
                        />
                        <Chip
                          color={details.overdue ? "error" : "default"}
                          variant="outlined"
                          label={`${tx("Срок", "Due")}: ${new Date(details.dueAt).toLocaleDateString(locale === "ru" ? "ru-RU" : "en-US")}`}
                        />
                      </Stack>
                      <Box className="task-status"
                        sx={{ display: "grid", gap: 1 }}
                      >
                        <Box
                          sx={{
                            textAlign: "center",
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 1, sm: 2 },
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: ".08em", textTransform: "uppercase" }}>
                            {tx("Статус задачи", "Task status")}
                          </Typography>
                          <TaskStatusDisplay
                            status={details.status}
                            archived={details.archived}
                            overdue={details.overdue}
                            completedLate={details.completedLate}
                            tx={tx}
                          />
                          {details.status === "done" && details.completedAt && (
                            <Tooltip
                              arrow
                              title={details.completedLate
                                ? tx(`Срок был ${formatMomentDay(details.dueAt, locale)}`, `Due date was ${formatMomentDay(details.dueAt, locale)}`)
                                : ""}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  display: "inline-block",
                                  color: details.completedLate ? "error.main" : "text.primary",
                                  fontWeight: details.completedLate ? 600 : 400,
                                }}
                              >
                                {tx("Дата выполнения", "Completed on")}: {formatMomentDay(details.completedAt, locale)}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                        {details.status === "done" && details.resolution && (
                          <Box
                            component={motion.div}
                            {...fadeUp}
                            sx={{
                              p: { xs: 1.75, sm: 2.25 },
                              borderRadius: uiRadii.card,
                              border: "1px solid",
                              borderColor: alpha(details.completedLate ? statusColors.completedLate : statusColors.completed, 0.35),
                              bgcolor: alpha(details.completedLate ? statusColors.completedLate : statusColors.completed, 0.06),
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", letterSpacing: ".08em", textTransform: "uppercase", mb: 0.75 }}>
                              {tx("Решение", "Resolution")}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 16,
                                lineHeight: 1.7,
                                color: "text.primary",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              {details.resolution}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box className="task-description"
                        sx={{
                          px: { xs: 0.25, sm: 0.5 },
                          py: { xs: 0.5, sm: 0.75 },
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, mb: 1.25 }}>
                          {tx("Описание задачи", "Task description")}
                        </Typography>
                        {details.description ? (
                          <Box
                            sx={{
                              fontSize: 16,
                              lineHeight: 1.7,
                              color: "text.primary",
                              "& p": { my: 0.6 },
                            }}
                            dangerouslySetInnerHTML={{
                              __html: sanitizeBasicHtml(details.description),
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {tx("Описание не добавлено", "No description")}
                          </Typography>
                        )}
                      </Box>
                      {attachments.length > 0 && (
                      <Box className="task-attachments"
                        sx={{
                          px: { xs: 1.5, sm: 2 },
                          py: 1.75,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 3,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, mb: 1.25 }}>
                          {tx("Вложения", "Attachments")}
                        </Typography>
                        <Stack spacing={1}>
                            {attachments.map((item) => (
                              <Box
                                key={item.id}
                                sx={{
                                  ...secondaryButtonSx,
                                  display: "inline-flex",
                                  alignSelf: "flex-start",
                                  alignItems: "center",
                                  gap: 1,
                                  px: 1.25,
                                  py: 0.75,
                                  maxWidth: "100%",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 2,
                                }}
                              >
                                <Typography variant="body2" sx={{ minWidth: 0, maxWidth: { xs: "calc(100vw - 150px)", sm: 520 }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {item.filename} · {(item.size / 1024).toFixed(1)} KB
                                </Typography>
                                <Tooltip title={tx("Скачать", "Download")}>
                                  <IconButton size="small" onClick={() => downloadAttachment(item)} aria-label={tx("Скачать вложение", "Download attachment")}>
                                    <DownloadRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ))}
                        </Stack>
                      </Box>
                      )}
                      <Box className="task-comments" sx={{ order: 5 }}>
                        <Box sx={{ py: 1.25 }}>
                          <Typography sx={{ fontWeight: 700 }}>
                            {tx("Комментарии", "Comments")}
                          </Typography>
                        </Box>
                        <Box sx={{ py: 1 }}>
                          <Stack spacing={0.75}>
                            {comments.length ? (
                              comments.map((c, index) => {
                                const own = c.authorEmail === session?.email;
                                const linkedFiles = attachments.filter(
                                  (item) => item.commentId === c.id,
                                );
                                const dayKey = commentDayKey(c.createdAt);
                                const startsDay =
                                  !index ||
                                  commentDayKey(comments[index - 1].createdAt) !== dayKey;
                                const next = comments[index + 1];
                                const showAvatar =
                                  next?.authorEmail !== c.authorEmail ||
                                  commentDayKey(next.createdAt) !== dayKey;
                                const attachmentOnly =
                                  [
                                    "__attachment__",
                                    "Вложение",
                                    "Attachment",
                                  ].includes(String(c.body || "").trim()) &&
                                  linkedFiles.length;
                                return (
                                  <React.Fragment key={c.id}>
                                  {startsDay && (
                                    <Box sx={{ display: "flex", justifyContent: "center", pt: index ? 1.25 : 0, pb: 0.5 }}>
                                      <Chip
                                        size="small"
                                        label={commentDayLabel(c.createdAt, locale, tx)}
                                        sx={{ bgcolor: "action.hover", color: "text.secondary", fontWeight: 500 }}
                                      />
                                    </Box>
                                  )}
                                  <Stack
                                    component={motion.div}
                                    initial={{ opacity: 0, y: 8, scale: 0.98 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    transition={{ duration: uiMotion.base, ease: uiMotion.ease }}
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="flex-end"
                                    sx={{
                                      columnGap: 1.25,
                                      alignSelf: own
                                        ? "flex-end"
                                        : "flex-start",
                                      width: "100%",
                                      maxWidth: { xs: "96%", sm: "78%" },
                                      flexDirection: own
                                        ? "row-reverse"
                                        : "row",
                                    }}
                                  >
                                    {showAvatar ? (
                                      <Tooltip title={c.authorName || c.authorEmail} arrow>
                                      <IconButton onClick={() => openUserProfile(c.authorEmail)} sx={{ p: 0, flexShrink: 0 }}>
                                      <Avatar
                                        src={c.authorAvatarUrl || ""}
                                        sx={{
                                          width: 30,
                                          height: 30,
                                          bgcolor: "ui.avatar",
                                          fontSize: 12,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {(c.authorName || c.authorEmail || "U")
                                          .charAt(0)
                                          .toUpperCase()}
                                      </Avatar>
                                      </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Box sx={{ width: 30, flexShrink: 0 }} />
                                    )}
                                    <Box
                                      sx={{
                                        minWidth: 88,
                                        maxWidth: "calc(100% - 38px)",
                                        bgcolor: own
                                          ? alpha(
                                              theme.palette.primary.main,
                                              mode === "dark" ? 0.24 : 0.1,
                                            )
                                          : "background.paper",
                                        color: "text.primary",
                                        px: 1.4,
                                        py: 0.75,
                                        border: "1px solid",
                                        borderColor: own
                                          ? alpha(
                                              theme.palette.primary.main,
                                              0.18,
                                            )
                                          : "divider",
                                        borderRadius: own
                                          ? "18px 18px 5px 18px"
                                          : "18px 18px 18px 5px",
                                        boxShadow:
                                          (t) => t.palette.ui.shadowSoft,
                                      }}
                                    >
                                      {!own && showAvatar && (
                                        <Typography
                                          component="button"
                                          onClick={() =>
                                            openUserProfile(c.authorEmail)
                                          }
                                          variant="caption"
                                          sx={{
                                            p: 0,
                                            border: 0,
                                            bgcolor: "transparent",
                                            cursor: "pointer",
                                            display: "block",
                                            mb: 0.25,
                                            color: "primary.main",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {c.authorName}
                                        </Typography>
                                      )}
                                      {!attachmentOnly && (
                                        <CommentBody
                                          body={c.body}
                                          own={own}
                                          users={users}
                                          onOpenProfile={openUserProfile}
                                        />
                                      )}
                                      {!!linkedFiles.length && (
                                        <Stack
                                          spacing={0.5}
                                          sx={{ mt: attachmentOnly ? 0 : 0.75 }}
                                        >
                                          {linkedFiles.map((item) =>
                                            String(item.contentType || "")
                                              .toLowerCase()
                                              .startsWith("image/") ? (
                                              <CommentImagePreview
                                                key={item.id}
                                                taskId={details.id}
                                                item={item}
                                                onDownload={downloadAttachment}
                                              />
                                            ) : (
                                              <Box
                                                key={item.id}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 0.5,
                                                  justifyContent: "flex-start",
                                                  px: 0.8,
                                                  py: 0.55,
                                                  borderRadius: 2,
                                                  color: "text.primary",
                                                  bgcolor: alpha(
                                                    theme.palette.primary.main,
                                                    0.08,
                                                  ),
                                                  textTransform: "none",
                                                  maxWidth: "100%",
                                                  fontWeight: 400,
                                                  "&:hover": {
                                                    bgcolor: alpha(
                                                      theme.palette.primary
                                                        .main,
                                                      0.14,
                                                    ),
                                                  },
                                                }}
                                              >
                                                <Box
                                                  component="span"
                                                  sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {tx("Скачать", "Download")}:{" "}
                                                  {item.filename}
                                                </Box>
                                                <Tooltip title={tx("Скачать", "Download")}>
                                                  <IconButton size="small" onClick={() => downloadAttachment(item)} aria-label={tx("Скачать вложение", "Download attachment")}>
                                                    <DownloadRoundedIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                              </Box>
                                            ),
                                          )}
                                        </Stack>
                                      )}
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: "block",
                                          textAlign: "right",
                                          mt: 0.2,
                                          color: "text.secondary",
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        {new Date(
                                          c.createdAt,
                                        ).toLocaleTimeString(
                                          locale === "ru" ? "ru-RU" : "en-US",
                                          {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          },
                                        )}
                                      </Typography>
                                    </Box>
                                    {currentRole === "admin" && (
                                      <Tooltip title={tx("Удалить комментарий", "Delete comment")} arrow>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => removeComment(c)}
                                          aria-label={tx("Удалить комментарий", "Delete comment")}
                                          sx={{ ...dangerIconButtonSx, width: 30, height: 30, flexShrink: 0, alignSelf: "center" }}
                                        >
                                          <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                  </React.Fragment>
                                );
                              })
                            ) : (
                              <Typography
                                color="text.secondary"
                                sx={{ textAlign: "center", py: 7 }}
                              >
                                {tx("Комментариев пока нет", "No comments yet")}
                              </Typography>
                            )}
                            <Box ref={commentsEndRef} />
                          </Stack>
                        </Box>
                        <Box
                          ref={commentComposerRef}
                          sx={{
                            position: "relative",
                            pt: 1.5,
                            bgcolor: "background.paper",
                            scrollMarginBottom: 16,
                          }}
                        >
                          <AnimatePresence>
                          {!!mentionUsers.length && (
                            <Paper
                              key="mentions"
                              component={motion.div}
                              {...popIn}
                              elevation={0}
                              sx={{
                                position: "absolute",
                                left: 12,
                                bottom: "calc(100% + 6px)",
                                width: { xs: "calc(100% - 24px)", sm: 330 },
                                p: 0.75,
                                borderRadius: uiRadii.card,
                                border: "1px solid",
                                borderColor: "divider",
                                boxShadow: (t) => t.palette.ui.shadowPopover,
                                transformOrigin: "bottom left",
                                zIndex: 3,
                              }}
                            >
                              {mentionUsers.map((user) => (
                                <Button
                                  key={user.email}
                                  fullWidth
                                  onClick={() => insertMention(user)}
                                  sx={{
                                    justifyContent: "flex-start",
                                    gap: 1,
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: uiRadii.item,
                                    color: "text.primary",
                                    fontWeight: 400,
                                  }}
                                >
                                  <Avatar
                                    src={user.avatarUrl || ""}
                                    sx={{ width: 28, height: 28, fontSize: 12 }}
                                  >
                                    {(user.displayName || user.email)
                                      .charAt(0)
                                      .toUpperCase()}
                                  </Avatar>
                                  <Box sx={{ textAlign: "left", minWidth: 0 }}>
                                    <Typography variant="body2" noWrap>
                                      {user.displayName || user.email}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                    >
                                      {user.email}
                                    </Typography>
                                  </Box>
                                </Button>
                              ))}
                            </Paper>
                          )}
                          </AnimatePresence>
                          {!!commentFiles.length && (
                            <Stack
                              direction="row"
                              spacing={0.75}
                              flexWrap="wrap"
                              useFlexGap
                              sx={{ px: 1, pb: 1 }}
                            >
                              {commentFiles.map((file, index) => (
                                <Chip
                                  key={`${file.name}-${index}`}
                                  size="small"
                                  icon={<AttachFileRoundedIcon />}
                                  label={file.name}
                                  onDelete={() =>
                                    setCommentFiles((current) =>
                                      current.filter(
                                        (_, itemIndex) => itemIndex !== index,
                                      ),
                                    )
                                  }
                                  deleteIcon={<CloseRoundedIcon />}
                                  sx={{ maxWidth: 240 }}
                                />
                              ))}
                            </Stack>
                          )}
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="flex-end"
                            sx={{
                              p: 0.5,
                              pl: 1,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: uiRadii.card,
                              bgcolor: "ui.subtle",
                              transition: "border-color .2s, box-shadow .2s",
                              "&:focus-within": {
                                borderColor: "primary.main",
                                boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.16)}`,
                              },
                            }}
                          >
                            <input
                              ref={commentFileRef}
                              hidden
                              type="file"
                              multiple
                              onChange={(event) => {
                                setCommentFiles((current) => [
                                  ...current,
                                  ...Array.from(event.target.files || []),
                                ]);
                                event.target.value = "";
                              }}
                            />
                            <Tooltip
                              title={tx("Прикрепить файл", "Attach file")}
                            >
                              <IconButton
                                onClick={() => commentFileRef.current?.click()}
                                sx={{ mb: 0.25 }}
                              >
                                <AttachFileRoundedIcon />
                              </IconButton>
                            </Tooltip>
                            <TextField
                              inputRef={commentInputRef}
                              fullWidth
                              multiline
                              maxRows={5}
                              variant="standard"
                              placeholder={tx(
                                "Сообщение…",
                                "Message…",
                              )}
                              value={comment}
                              onFocus={revealCommentComposer}
                              onChange={(e) => setComment(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  if (mentionUsers.length)
                                    insertMention(mentionUsers[0]);
                                  else sendComment();
                                }
                              }}
                              InputProps={{ disableUnderline: true }}
                              sx={{ py: 0.7 }}
                            />
                            <Tooltip title={tx("Отправить", "Send")}>
                              <span>
                                <IconButton
                                  color="primary"
                                  disabled={
                                    commentSending ||
                                    (!comment.trim() && !commentFiles.length)
                                  }
                                  onClick={sendComment}
                                  sx={{
                                    mb: 0.25,
                                    bgcolor: "primary.main",
                                    color: "primary.contrastText",
                                    "&:hover": { bgcolor: "primary.dark" },
                                    "&.Mui-disabled": {
                                      bgcolor: "action.disabledBackground",
                                    },
                                  }}
                                >
                                  {commentSending ? (
                                    <CircularProgress
                                      size={20}
                                      color="inherit"
                                    />
                                  ) : (
                                    <SendRoundedIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Box>
                      </Box>
                    </Stack>
                  </DialogContent>
                </Paper>
              </Box>
          </Box>
        )}

        <AccountDialog
          open={accountOpen}
          currentLocale={locale}
          currentTheme={mode}
          account={{ ...accountForm, email: session?.email }}
          loading={false}
          dirty={JSON.stringify(accountForm) !== JSON.stringify(settings)}
          enforceTwoFactor={!!settings.enforceTwoFactor}
          onClose={() => setAccountOpen(false)}
          onChange={(key, value) =>
            setAccountForm((v) => ({ ...v, [key]: value }))
          }
          onSave={saveAccount}
          onReset={() => setAccountForm(settings)}
          onAvatarUpload={uploadAvatar}
          onAvatarRemove={() =>
            setAccountForm((v) => ({ ...v, avatarUrl: "" }))
          }
          onOpenChangePassword={() => setPasswordDialogOpen(true)}
        />

        <ChangePasswordDialog
          open={passwordDialogOpen}
          currentLocale={locale}
          currentTheme={mode}
          onClose={() => setPasswordDialogOpen(false)}
          onChanged={() => notify(tx("Пароль изменён", "Password changed"))}
        />

        <UserProfileDialog
          state={userProfile}
          canEdit={!!userProfile.edit && currentRole === "admin"}
          tx={tx}
          onClose={() =>
            setUserProfile((current) => ({ ...current, open: false }))
          }
          onChange={(key, value) =>
            setUserProfile((current) => ({
              ...current,
              profile: { ...current.profile, [key]: value },
            }))
          }
          onSave={saveUserProfile}
        />

        <ConfirmationDialog
          confirmation={confirmation}
          onClose={() => setConfirmation(null)}
          tx={tx}
        />
        <SkeletonFooter currentYear={new Date().getFullYear()} />
        <AnimatePresence>
          {listVisible && showScrollTop && (
            <Box
              component={motion.div}
              key="scroll-top"
              initial={{ opacity: 0, y: 16, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.9 }}
              transition={{ duration: uiMotion.fast, ease: uiMotion.ease }}
              sx={{ position: "fixed", right: { xs: 16, md: 28 }, bottom: { xs: 20, md: 28 }, zIndex: 1250 }}
            >
              <Tooltip title={tx("Наверх", "Back to top")} arrow placement="left">
                <IconButton
                  aria-label={tx("Наверх", "Back to top")}
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  sx={{
                    ...iconButtonSx,
                    width: 46,
                    height: 46,
                    bgcolor: "background.paper",
                    boxShadow: (t) => t.palette.ui.shadowHover,
                    "&:hover": { bgcolor: "background.paper", transform: "translateY(-2px)" },
                  }}
                >
                  <KeyboardArrowUpRoundedIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </AnimatePresence>
        <Snackbar
          open={snack.open}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnack((v) => ({ ...v, open: false }))}
        >
          <Alert
            severity={snack.severity}
            variant="filled"
            sx={{ boxShadow: (t) => t.palette.ui.shadowPopover }}
            onClose={() => setSnack((v) => ({ ...v, open: false }))}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
